import React, { useState, useEffect, useRef } from 'react'
import { Upload, FileText, Calendar, DollarSign, AlertCircle, CheckCircle, Clock, Trash2, Eye, X, Users, Shield, Building, Plus, Bell, BellOff, Settings, Archive, BarChart3, TrendingUp, RefreshCw, Lightbulb, CheckCircle2, XCircle, ChevronUp, ChevronDown, Download, Copy, Search, Filter, Edit } from 'lucide-react'
import type { ContractDocument, ContractRenewalSettings, ContractAnalytics, ContractInsight, CustomerContract, CustomerContractsAnalytics } from '../lib/storage'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import ContractManagementDashboard from '../components/ContractManagementDashboard'
import VendorRiskManagementDashboard from '../components/VendorRiskManagementDashboard'
import { ToastContainer, type ToastMessage } from '../components/Toast'
import { fetchWithAuth } from '../lib/api'

// Helper to create fetch options with credentials for auth cookie
const createFetchOptions = (options: RequestInit = {}): RequestInit => {
  const headers: Record<string, string> = {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    ...(options.headers as Record<string, string> || {})
  }

  // Add Authorization header if token exists in localStorage
  const token = localStorage.getItem('auth-token')
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return {
    ...options,
    credentials: 'include',
    headers
  }
}

// Add cache-busting parameter to URL to bypass cached 404s
const cacheBust = (url: string) => {
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}_=${Date.now()}`
}

// Helper to safely extract values that might be nested {value, confidence, source} objects
// Handles both formats: "Zuora" and {value: "Zuora", confidence: 95, source: "..."}
const safeExtractValue = <T,>(field: any): T | undefined => {
  if (field === null || field === undefined) {
    return undefined
  }

  // If it's an object with a 'value' property, extract it
  if (typeof field === 'object' && field !== null && 'value' in field) {
    return field.value as T
  }

  // Otherwise return the field as-is
  return field as T
}

interface ContractsManagementProps {
  initialTab?: 'insights' | 'processing' | 'settings' | 'customers' | 'duplicates'
}

export default function ContractsManagement({ initialTab }: ContractsManagementProps = {}) {
  const [contracts, setContracts] = useState<ContractDocument[]>([])
  const [totalContracts, setTotalContracts] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [currentOffset, setCurrentOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploadingFiles, setUploadingFiles] = useState(false)
  const [filter, setFilter] = useState<'all' | 'active' | 'expiring' | 'archived'>('all')
  const [extractionStatusFilter, setExtractionStatusFilter] = useState<'all' | 'pending' | 'processing' | 'processed' | 'failed'>('all')
  const [renewalYearFilter, setRenewalYearFilter] = useState<string>('all') // 'all' or a specific year like '2025'
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>('all') // 'all', 'VENDOR_CONTRACT', 'MUTUAL_NDA', 'INVESTMENT_DOCUMENT', 'OTHER'
  const [contractCategoryFilter, setContractCategoryFilter] = useState<'all' | 'vendors' | 'customers' | 'duplicates'>('all') // Filter for contract category
  const [vendorSearchTerm, setVendorSearchTerm] = useState<string>('') // Search term for filtering vendors
  const [fromCurrentDateOnly, setFromCurrentDateOnly] = useState<boolean>(false) // Filter for contracts from current date onwards
  const [hasSeatsOnly, setHasSeatsOnly] = useState<boolean>(false) // Filter for contracts with seat counts
  const [seatCountOperator, setSeatCountOperator] = useState<'>' | '<' | '=' | 'all'>('all') // Seat count comparison operator
  const [seatCountValue, setSeatCountValue] = useState<number>(0) // Seat count value to compare against
  const [extractingContracts, setExtractingContracts] = useState<Set<string>>(new Set())
  const [selectedContract, setSelectedContract] = useState<ContractDocument | null>(null)
  const [showRenewalSettings, setShowRenewalSettings] = useState(false)
  const [renewalSettings, setRenewalSettings] = useState<ContractRenewalSettings | null>(null)
  const [nonRenewalContract, setNonRenewalContract] = useState<ContractDocument | null>(null)
  const [analytics, setAnalytics] = useState<ContractAnalytics | null>(null)
  const [archiveYear, setArchiveYear] = useState<number | null>(null)
  const [insights, setInsights] = useState<ContractInsight[]>([])
  const [generatingInsights, setGeneratingInsights] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [showInsightsModal, setShowInsightsModal] = useState(false)
  const [selectedContractIds, setSelectedContractIds] = useState<Set<string>>(new Set())
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null)
  const [reprocessingContracts, setReprocessingContracts] = useState(false)
  const [reprocessProgress, setReprocessProgress] = useState<{ current: number; total: number } | null>(null)
  const [activeJob, setActiveJob] = useState<{
    id: string
    totalContracts: number
    processedCount: number
    currentContract?: string
    status: 'running' | 'completed' | 'failed'
  } | null>(null)
  const [activeTab, setActiveTab] = useState<'insights' | 'processing' | 'settings' | 'customers' | 'duplicates'>(initialTab || 'settings')
  const [contractSettings, setContractSettings] = useState<{ employeeCount?: number } | null>(null)
  const [savingSettings, setSavingSettings] = useState(false)
  const [settingsSaved, setSettingsSaved] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0) // Increment to trigger dashboard refresh
  const [totalPendingCount, setTotalPendingCount] = useState(0)
  const [totalProcessedCount, setTotalProcessedCount] = useState(0)
  // Individual extraction status counts for filtering
  const [extractionCounts, setExtractionCounts] = useState({
    pending: 0,
    processing: 0,
    processed: 0,
    failed: 0
  })
  // Duplicate detection state
  const [detectingDuplicates, setDetectingDuplicates] = useState(false)
  const [duplicateDetectionResults, setDuplicateDetectionResults] = useState<{
    duplicatesFound: number
    duplicateGroups: any[]
  } | null>(null)
  // Sorting state for Processing tab
  const [sortColumn, setSortColumn] = useState<'status' | 'vendor' | 'documentType' | 'fileName' | 'value' | 'seats' | 'renewal' | 'uploaded' | 'contractDate' | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Toast notification state
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  // Track contracts currently being extracted for UI indicators
  const [contractsBeingExtracted, setContractsBeingExtracted] = useState<Set<string>>(new Set())

  // Customer Contracts state (revenue attribution)
  const [customerContracts, setCustomerContracts] = useState<CustomerContract[]>([])
  const [customerAnalytics, setCustomerAnalytics] = useState<CustomerContractsAnalytics | null>(null)
  const [customerContractsLoading, setCustomerContractsLoading] = useState(false)
  const [customerContractsError, setCustomerContractsError] = useState<string | null>(null)
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false)
  const [showImportCustomerCSV, setShowImportCustomerCSV] = useState(false)
  const [selectedCustomerContract, setSelectedCustomerContract] = useState<CustomerContract | null>(null)

  // Customer Contracts search, filter, and sort state
  const [customerSearchQuery, setCustomerSearchQuery] = useState('')
  const [customerStatusFilter, setCustomerStatusFilter] = useState<'all' | 'active' | 'churned'>('all')
  const [customerSortColumn, setCustomerSortColumn] = useState<'customerName' | 'contractValue' | 'startDate' | 'renewalDate' | null>(null)
  const [customerSortDirection, setCustomerSortDirection] = useState<'asc' | 'desc'>('asc')

  // Bulk edit state
  const [showBulkEditToolbar, setShowBulkEditToolbar] = useState(false)
  const [bulkEditDocumentType, setBulkEditDocumentType] = useState<string>('')
  const [bulkEditITSpend, setBulkEditITSpend] = useState<boolean | null>(null)
  const [bulkEditArchive, setBulkEditArchive] = useState<boolean | null>(null)
  const [bulkUpdating, setBulkUpdating] = useState(false)

  // Toast notification helpers
  const addToast = (toast: Omit<ToastMessage, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random()}`
    console.log(`[addToast] Creating toast:`, { id, type: toast.type, title: toast.title })
    setToasts(prev => {
      const newToasts = [...prev, { ...toast, id }]
      console.log(`[addToast] Toast state updated. Total toasts:`, newToasts.length)
      return newToasts
    })
  }

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }

  // Check authentication before loading data
  useEffect(() => {
    const checkAuth = async () => {
      console.log('[ContractsManagement] 🔐 Checking authentication...')
      try {
        const token = localStorage.getItem('auth-token')

        // Check for valid session - will check cookie if no Authorization header
        const headers: HeadersInit = {
          'Content-Type': 'application/json'
        }

        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }

        const response = await fetch('/api/auth/validate', {
          method: 'GET',
          credentials: 'include', // Important: includes cookies for OAuth auth
          headers
        })

        console.log('[ContractsManagement] 🔐 Auth check response:', response.status, response.ok)

        if (!response.ok) {
          // Session expired or invalid - redirect to login
          console.log('[ContractsManagement] 🔐 Session expired, redirecting to login...')
          localStorage.removeItem('auth-token')
          localStorage.removeItem('admin-session')
          window.location.href = '/admin'
          return
        }

        setIsAuthenticated(true)
      } catch (err) {
        console.error('[ContractsManagement] 🔐 Auth check failed:', err)
        localStorage.removeItem('auth-token')
        localStorage.removeItem('admin-session')
        window.location.href = '/admin'
      }
    }
    checkAuth()
  }, [])

  // Customer Contracts data loading functions
  const loadCustomerContracts = async () => {
    setCustomerContractsLoading(true)
    setCustomerContractsError(null)
    try {
      const response = await fetch('/api/customer-contracts', createFetchOptions())

      // Gracefully handle 401/404 - feature may not be available yet
      if (response.status === 401 || response.status === 404) {
        console.log('📋 Customer contracts feature not available')
        setCustomerContracts([])
        return
      }

      if (!response.ok) throw new Error('Failed to load customer contracts')

      const data = await response.json()
      setCustomerContracts(data.contracts || [])
    } catch (err) {
      // Only show error toast for unexpected errors (not 401/404)
      console.error('Failed to load customer contracts:', err)
      const errorMsg = err instanceof Error ? err.message : 'Unknown error'
      setCustomerContractsError(errorMsg)
    } finally {
      setCustomerContractsLoading(false)
    }
  }

  const loadCustomerAnalytics = async () => {
    try {
      const response = await fetch('/api/customer-revenue-analytics', createFetchOptions())

      // Gracefully handle 401/404 - feature may not be available yet
      if (response.status === 401 || response.status === 404) {
        console.log('📊 Customer analytics feature not available')
        return
      }

      if (!response.ok) throw new Error('Failed to load analytics')

      const data = await response.json()
      setCustomerAnalytics(data)
    } catch (err) {
      console.error('Failed to load customer analytics:', err)
      const errorMsg = err instanceof Error ? err.message : 'Unknown error'
      addToast({
        type: 'error',
        title: 'Failed to load analytics',
        message: errorMsg
      })
    }
  }

  const handleCreateCustomerContract = async (contractData: Omit<CustomerContract, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const response = await fetch('/api/customer-contracts', createFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contractData)
      }))

      if (!response.ok) {
        let errorMessage = 'Failed to create contract'
        try {
          const error = await response.json()
          errorMessage = error.error || errorMessage
        } catch (parseErr) {
          // If JSON parsing fails, try to get text response
          try {
            errorMessage = await response.text() || errorMessage
          } catch {
            // Fallback to default error message
          }
        }
        throw new Error(errorMessage)
      }

      await loadCustomerContracts()
      await loadCustomerAnalytics()

      addToast({
        type: 'success',
        title: 'Contract created',
        message: `Created contract for ${contractData.customerName}`
      })

      setShowAddCustomerModal(false)
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error'
      addToast({
        type: 'error',
        title: 'Failed to create contract',
        message: errorMsg
      })
    }
  }

  const handleUpdateCustomerContract = async (contract: CustomerContract) => {
    try {
      const response = await fetch('/api/customer-contracts', createFetchOptions({
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contract)
      }))

      if (!response.ok) {
        let errorMessage = 'Failed to update contract'
        try {
          const error = await response.json()
          errorMessage = error.error || errorMessage
        } catch (parseErr) {
          // If JSON parsing fails, try to get text response
          try {
            errorMessage = await response.text() || errorMessage
          } catch {
            // Fallback to default error message
          }
        }
        throw new Error(errorMessage)
      }

      await loadCustomerContracts()
      await loadCustomerAnalytics()

      addToast({
        type: 'success',
        title: 'Contract updated',
        message: `Updated contract for ${contract.customerName}`
      })

      setSelectedCustomerContract(null)
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error'
      addToast({
        type: 'error',
        title: 'Failed to update contract',
        message: errorMsg
      })
    }
  }

  const handleDeleteCustomerContract = async (contractId: string, customerName: string) => {
    if (!confirm(`Are you sure you want to delete the contract for ${customerName}?`)) {
      return
    }

    try {
      const response = await fetch(`/api/customer-contracts?id=${contractId}`, createFetchOptions({
        method: 'DELETE'
      }))

      if (!response.ok) {
        let errorMessage = 'Failed to delete contract'
        try {
          const error = await response.json()
          errorMessage = error.error || errorMessage
        } catch (parseErr) {
          // If JSON parsing fails, try to get text response
          try {
            errorMessage = await response.text() || errorMessage
          } catch {
            // Fallback to default error message
          }
        }
        throw new Error(errorMessage)
      }

      await loadCustomerContracts()
      await loadCustomerAnalytics()

      addToast({
        type: 'success',
        title: 'Contract deleted',
        message: `Deleted contract for ${customerName}`
      })
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error'
      addToast({
        type: 'error',
        title: 'Failed to delete contract',
        message: errorMsg
      })
    }
  }

  // Handle CSV import for customer contracts
  const handleCustomerCSVImport = async (file: File) => {
    try {
      // Parse CSV file
      const text = await file.text()
      const lines = text.split('\n').filter(line => line.trim())

      if (lines.length < 2) {
        addToast({ type: 'error', title: 'Invalid CSV', message: 'CSV file must have a header row and at least one data row' })
        return
      }

      // Parse header
      const headers = lines[0].split(',').map(h => h.trim())

      // Parse data rows
      const csvData = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim())
        const row: any = {}
        headers.forEach((header, index) => {
          row[header] = values[index] || ''
        })
        return row
      })

      // Send to backend
      const response = await fetch('/api/customer-contracts-import', createFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csvData })
      }))

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to import contracts')
      }

      const result = await response.json()

      // Show results
      if (result.failed > 0) {
        addToast({
          type: 'warning',
          title: 'Import completed with errors',
          message: `Imported ${result.imported} contracts, ${result.failed} failed. Check console for details.`
        })
        console.log('Import results:', result.results)
      } else {
        addToast({
          type: 'success',
          title: 'Import successful',
          message: `Successfully imported ${result.imported} contracts`
        })
      }

      // Refresh data
      await loadCustomerContracts()
      await loadCustomerAnalytics()
      setShowImportCustomerCSV(false)
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error'
      addToast({ type: 'error', title: 'Import failed', message: errorMsg })
    }
  }

  // Load customer contracts when tab becomes active
  useEffect(() => {
    if (activeTab === 'customers' && isAuthenticated) {
      loadCustomerContracts()
      loadCustomerAnalytics()
    }
  }, [activeTab, isAuthenticated])

  // Check for active reprocessing jobs on page load
  useEffect(() => {
    if (!isAuthenticated) return

    const loadActiveJob = async () => {
      try {
        const response = await fetch(cacheBust('/api/tprm/contracts/reprocess-job'), createFetchOptions())
        if (response.ok) {
          const job = await response.json()
          if (job && job.status === 'running') {
            console.log('📊 Restored active reprocessing job:', job)
            setActiveJob(job)
            setReprocessingContracts(true)
            setReprocessProgress({
              current: job.processedCount,
              total: job.totalContracts
            })
          }
        }
      } catch (error) {
        console.error('Failed to load active job:', error)
      }
    }

    loadActiveJob()
  }, [isAuthenticated])

  // Load contracts and settings only after authentication
  useEffect(() => {
    console.log('[ContractsManagement] ⚡ Data loading effect triggered. isAuthenticated:', isAuthenticated, 'filter:', filter)
    if (!isAuthenticated) {
      console.log('[ContractsManagement] ⚠️  Skipping data load - not authenticated')
      return
    }

    console.log('[ContractsManagement] ✅ Authentication verified, loading data...')
    setCurrentOffset(0) // Reset pagination when filter or tab changes
    loadContracts()
    loadRenewalSettings()
    loadAnalytics() // Load analytics for Dashboard tab
    loadInsights() // Load insights for Insights tab
  }, [filter, activeTab, contractCategoryFilter, isAuthenticated])

  // Poll for extraction status updates
  useEffect(() => {
    if (!isAuthenticated) return
    if (extractingContracts.size === 0) return

    const interval = setInterval(async () => {
      // Check status of each extracting contract
      for (const contractId of extractingContracts) {
        try {
          const response = await fetch(cacheBust(`/api/tprm/contracts/${contractId}/extraction-status`), createFetchOptions())
          if (response.ok) {
            const data = await response.json()
            if (data.status === 'completed' || data.status === 'failed') {
              // Remove from extracting set
              setExtractingContracts(prev => {
                const next = new Set(prev)
                next.delete(contractId)
                return next
              })
              // Reload contracts to show updated data
              await loadContracts()
            }
          }
        } catch (err) {
          console.error(`Failed to check extraction status for ${contractId}:`, err)
        }
      }
    }, 5000) // Poll every 5 seconds

    return () => clearInterval(interval)
  }, [extractingContracts, isAuthenticated])

  // Load and poll extraction counts for background processing info
  useEffect(() => {
    if (!isAuthenticated) return

    // Load initial counts
    loadExtractionCounts()

    // Poll every 30 seconds to show background processing progress
    const interval = setInterval(() => {
      loadExtractionCounts()
    }, 30000) // Poll every 30 seconds

    return () => clearInterval(interval)
  }, [isAuthenticated])

  // Auto-refresh during extraction
  useEffect(() => {
    if (contractsBeingExtracted.size === 0) return

    const intervalId = setInterval(async () => {
      console.log(`[Auto-refresh] Refreshing contracts (${contractsBeingExtracted.size} extracting)`)
      await loadContracts()
    }, 8000) // Poll every 8 seconds

    return () => clearInterval(intervalId)
  }, [contractsBeingExtracted.size])

  // Handle column header click for sorting
  const handleSort = (column: 'status' | 'vendor' | 'documentType' | 'fileName' | 'value' | 'renewal' | 'uploaded' | 'contractDate' | 'seats') => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new column and default to ascending
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const handleReprocessSelected = async () => {
    if (selectedContractIds.size === 0) {
      alert('Please select contracts to re-process')
      return
    }

    if (!confirm(`Re-process ${selectedContractIds.size} selected contract${selectedContractIds.size !== 1 ? 's' : ''}? This will trigger AI extraction again.`)) {
      return
    }

    setReprocessingContracts(true)
    setReprocessProgress(null)

    const contractIdsArray = Array.from(selectedContractIds)
    let successCount = 0
    let failCount = 0
    const errors: string[] = []

    console.log(`🔄 Re-processing ${contractIdsArray.length} contracts sequentially...`)

    // Process sequentially with delays to avoid overwhelming Lambda
    for (let i = 0; i < contractIdsArray.length; i++) {
      const contractId = contractIdsArray[i]

      setReprocessProgress({
        current: i + 1,
        total: contractIdsArray.length
      })

      console.log(`  🔄 Re-processing ${i + 1}/${contractIdsArray.length}: ${contractId}`)

      // Add to tracking set before extraction
      setContractsBeingExtracted(prev => new Set([...prev, contractId]))

      try {
        await triggerExtraction(contractId)
        successCount++
        console.log(`  ✅ Success: ${contractId}`)

        // Show success toast
        console.log(`[Toast] About to add success toast for ${contractId}`)
        const contract = contracts.find(c => c.id === contractId)
        const vendorName = contract?.vendorName || contract?.fileName || 'Contract'
        console.log(`[Toast] Found contract vendor: ${vendorName}`)
        addToast({
          type: 'success',
          title: 'Extraction complete',
          message: `${vendorName} processed successfully`
        })
        console.log(`[Toast] Success toast added`)

        // Remove from tracking set
        setContractsBeingExtracted(prev => {
          const next = new Set(prev)
          next.delete(contractId)
          return next
        })
      } catch (error) {
        failCount++
        const errorMsg = error instanceof Error ? error.message : 'Unknown error'
        errors.push(`Contract ${i + 1}: ${errorMsg}`)
        console.error(`  ❌ Failed: ${contractId}`, error)

        // Show error toast
        addToast({
          type: 'error',
          title: 'Extraction failed',
          message: errorMsg
        })

        // Remove from tracking set
        setContractsBeingExtracted(prev => {
          const next = new Set(prev)
          next.delete(contractId)
          return next
        })
      }

      // Add delay between extractions to avoid Claude API rate limits
      if (i < contractIdsArray.length - 1) {
        const delay = 10000 // 10 seconds between extractions
        console.log(`  ⏸️  Waiting ${delay / 1000}s before next extraction...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }

    setReprocessProgress(null)
    setReprocessingContracts(false)

    console.log(`✅ Re-processing complete: ${successCount} succeeded, ${failCount} failed`)

    // Show summary toast
    if (failCount === 0) {
      addToast({
        type: 'success',
        title: 'All extractions complete',
        message: `${successCount} contract${successCount !== 1 ? 's' : ''} processed successfully`,
        duration: 7000
      })
    } else {
      addToast({
        type: 'info',
        title: 'Processing complete',
        message: `${successCount} succeeded, ${failCount} failed`,
        duration: 10000
      })
    }

    // Clear selection and reload
    setSelectedContractIds(new Set())
    await loadContracts()
  }

  const handleProcessAllPending = async () => {
    // Get all contracts with pending extraction status
    const pendingContracts = contracts.filter(c => c.extractionStatus === 'pending')

    if (pendingContracts.length === 0) {
      alert('No pending contracts to process')
      return
    }

    if (!confirm(`Process ${pendingContracts.length} pending contract${pendingContracts.length !== 1 ? 's' : ''}? This will trigger AI extraction for all pending contracts.`)) {
      return
    }

    setReprocessingContracts(true)
    setReprocessProgress(null)

    let successCount = 0
    let failCount = 0
    const errors: string[] = []

    console.log(`🔄 Processing ${pendingContracts.length} pending contracts sequentially...`)

    // Create persistent job record
    try {
      const createJobResponse = await fetch('/api/tprm/contracts/reprocess-job', createFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          totalContracts: pendingContracts.length,
          processedCount: 0,
          status: 'running'
        })
      }))

      if (createJobResponse.ok) {
        const job = await createJobResponse.json()
        setActiveJob(job)
        console.log('📊 Created persistent reprocessing job:', job.id)
      }
    } catch (error) {
      console.error('Failed to create job record:', error)
      // Continue anyway - job tracking is optional
    }

    // Process sequentially with delays to avoid Claude API rate limits
    for (let i = 0; i < pendingContracts.length; i++) {
      const contract = pendingContracts[i]

      setReprocessProgress({
        current: i + 1,
        total: pendingContracts.length
      })

      console.log(`  🔄 Processing ${i + 1}/${pendingContracts.length}: ${contract.id}`)

      try {
        await triggerExtraction(contract.id)
        successCount++
        console.log(`  ✅ Success: ${contract.id}`)
      } catch (error) {
        failCount++
        const errorMsg = error instanceof Error ? error.message : 'Unknown error'
        errors.push(`${contract.fileName}: ${errorMsg}`)
        console.error(`  ❌ Failed: ${contract.id}`, error)
      }

      // Update persistent job record
      try {
        await fetch('/api/tprm/contracts/reprocess-job', createFetchOptions({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            processedCount: i + 1,
            currentContract: contract.fileName,
            status: 'running'
          })
        }))
      } catch (error) {
        console.error('Failed to update job record:', error)
        // Continue anyway
      }

      // Add delay between extractions to avoid Claude API rate limits
      if (i < pendingContracts.length - 1) {
        const delay = 10000 // 10 seconds between extractions
        console.log(`  ⏸️  Waiting ${delay / 1000}s before next extraction...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }

    setReprocessProgress(null)
    setReprocessingContracts(false)
    setActiveJob(null)

    // Delete job record when complete
    try {
      await fetch('/api/tprm/contracts/reprocess-job', createFetchOptions({
        method: 'DELETE'
      }))
      console.log('🗑️  Deleted reprocessing job record')
    } catch (error) {
      console.error('Failed to delete job record:', error)
    }

    // Show summary
    let message = `Processing complete!\n\n${successCount} succeeded, ${failCount} failed`
    if (errors.length > 0) {
      message += `\n\nErrors:\n${errors.slice(0, 5).join('\n')}`
      if (errors.length > 5) {
        message += `\n... and ${errors.length - 5} more errors`
      }
    }
    alert(message)

    await loadContracts()
  }

  const triggerExtraction = async (contractId: string) => {
    try {
      // Add to extracting set
      setExtractingContracts(prev => new Set(prev).add(contractId))

      const response = await fetch(cacheBust(`/api/tprm/contracts/${contractId}/extract`), createFetchOptions({
        method: 'POST'
      }))

      if (!response.ok) {
        let errorMsg = `Failed to trigger extraction (${response.status})`
        try {
          const errorData = await response.json()
          console.error(`  ❌ Server error details for ${contractId}:`, errorData)
          errorMsg = errorData.error || errorData.message || errorMsg
          if (errorData.stack) {
            console.error(`  📋 Stack trace:`, errorData.stack)
          }
        } catch (parseError) {
          console.error(`  ❌ Could not parse error response (likely not JSON)`)
        }
        throw new Error(errorMsg)
      }

      console.log(`Started extraction for contract ${contractId}`)
    } catch (err) {
      console.error('Extraction trigger error:', err)
      // Remove from extracting set on error
      setExtractingContracts(prev => {
        const next = new Set(prev)
        next.delete(contractId)
        return next
      })
      // Re-throw so batch processing can track failures
      throw err
    }
  }

  const loadContracts = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams()

      if (filter === 'archived') {
        params.set('archived', 'true')
      } else if (filter === 'expiring') {
        // Don't send expiring=true to backend (causes timeout)
        // Will filter client-side for contracts expiring within 90 days
      } else if (filter === 'active') {
        params.set('status', 'active')
      }

      // Handle contract category filter
      if (contractCategoryFilter === 'duplicates') {
        params.set('duplicates', 'true')
      } else if (contractCategoryFilter === 'customers') {
        params.set('category', 'customer')
        params.set('duplicates', 'false')
      } else if (contractCategoryFilter === 'vendors') {
        params.set('category', 'vendor')
        params.set('duplicates', 'false')
      } else {
        params.set('duplicates', 'false')
      }

      const url = cacheBust(`/api/tprm/contracts-db?${params.toString()}`)
      console.log('[ContractsManagement] 📡 Loading contracts from:', url)
      const response = await fetch(url, createFetchOptions())
      console.log('[ContractsManagement] 📡 Contracts response status:', response.status, response.statusText)

      if (!response.ok) {
        throw new Error('Failed to load contracts')
      }

      const data = await response.json()
      setContracts(data.contracts || [])
      setTotalContracts(data.total || 0)
      setHasMore(data.hasMore || false)
      setCurrentOffset(data.contracts?.length || 0)

      console.log(`[ContractsManagement] 📊 Loaded ${data.contracts?.length || 0} of ${data.total || 0} contracts`)
    } catch (err) {
      console.error('Error loading contracts:', err)
      setError(err instanceof Error ? err.message : 'Failed to load contracts')
    } finally {
      setLoading(false)
      // Increment refresh trigger to update dashboards
      setRefreshTrigger(prev => prev + 1)
    }
  }

  const loadExtractionCounts = async () => {
    try {
      // Use dedicated lightweight extraction counts endpoint
      const countsUrl = cacheBust('/api/tprm/contracts/extraction-counts')
      const countsResponse = await fetch(countsUrl, createFetchOptions())

      if (countsResponse.ok) {
        const countsData = await countsResponse.json()

        const pending = countsData.pending || 0
        const processing = countsData.processing || 0
        const processed = (countsData.processed || 0) + (countsData.completed || 0) // Handle both new and old field names
        const failed = countsData.failed || 0

        setTotalPendingCount(pending + processing) // Include processing as "pending"
        setTotalProcessedCount(processed + failed) // Include failed as "processed"

        // Store individual counts for filtering
        setExtractionCounts({ pending, processing, processed, failed })

        console.log(`📊 Extraction counts: ${pending} pending, ${processing} processing, ${processed} processed, ${failed} failed`)
      } else {
        console.warn('Extraction counts endpoint returned error:', countsResponse.status)
        // Fallback: count from loaded contracts
        const pendingCount = contracts.filter(c => c.extractionStatus === 'pending' || c.extractionStatus === 'processing').length
        const processedCount = contracts.filter(c => c.extractionStatus === 'processed' || c.extractionStatus === 'completed' || c.extractionStatus === 'failed').length
        setTotalPendingCount(pendingCount)
        setTotalProcessedCount(processedCount)
      }
    } catch (err) {
      console.error('Error loading extraction counts:', err)
      // Fallback: count from loaded contracts
      const pendingCount = contracts.filter(c => c.extractionStatus === 'pending' || c.extractionStatus === 'processing').length
      const processedCount = contracts.filter(c => c.extractionStatus === 'completed' || c.extractionStatus === 'failed').length
      setTotalPendingCount(pendingCount)
      setTotalProcessedCount(processedCount)
    }
  }

  const loadMoreContracts = async () => {
    if (!hasMore || loadingMore) return

    try {
      setLoadingMore(true)

      const params = new URLSearchParams()
      params.set('offset', currentOffset.toString())
      params.set('limit', '20')

      if (filter === 'archived') {
        params.set('archived', 'true')
      } else if (filter === 'expiring') {
        // Don't send expiring=true to backend (causes timeout)
        // Will filter client-side for contracts expiring within 90 days
      } else if (filter === 'active') {
        params.set('status', 'active')
      }

      const url = cacheBust(`/api/tprm/contracts-db?${params.toString()}`)
      console.log('[ContractsManagement] 📡 Loading more contracts from:', url)
      const response = await fetch(url, createFetchOptions())

      if (!response.ok) {
        throw new Error('Failed to load more contracts')
      }

      const data = await response.json()
      setContracts(prev => [...prev, ...(data.contracts || [])])
      setHasMore(data.hasMore || false)
      setCurrentOffset(currentOffset + (data.contracts?.length || 0))

      console.log(`[ContractsManagement] 📊 Loaded ${data.contracts?.length || 0} more contracts (total: ${contracts.length + (data.contracts?.length || 0)} of ${data.total || 0})`)
    } catch (err) {
      console.error('Error loading more contracts:', err)
      alert('Failed to load more contracts: ' + (err instanceof Error ? err.message : 'Unknown error'))
    } finally {
      setLoadingMore(false)
    }
  }

  const extractYearFromPath = (filePath: string): number | null => {
    // Try to extract 4-digit year from path
    // Matches: /2023/, /contracts-2024/, /archive_2022/, etc.
    const yearMatch = filePath.match(/\b(20\d{2})\b/)
    if (yearMatch) {
      const year = parseInt(yearMatch[1])
      const currentYear = new Date().getFullYear()
      // Only accept reasonable years (2000-current year + 5)
      if (year >= 2000 && year <= currentYear + 5) {
        return year
      }
    }
    return null
  }

  const handleFolderUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    // Filter for allowed file types and extract folder info
    const allowedExtensions = ['.pdf', '.docx', '.doc', '.txt']
    const validFiles: Array<{ file: File; folderPath: string; year: number | null }> = []

    for (const file of Array.from(files)) {
      const fileName = file.name.toLowerCase()
      const isValidType = allowedExtensions.some(ext => fileName.endsWith(ext))

      if (isValidType) {
        // @ts-ignore - webkitRelativePath exists on File from folder input
        const folderPath = file.webkitRelativePath || file.name
        const year = extractYearFromPath(folderPath)
        validFiles.push({ file, folderPath, year })
      }
    }

    if (validFiles.length === 0) {
      alert('No valid contract files found in folder. Only PDF, DOCX, and TXT files are supported.')
      event.target.value = ''
      return
    }

    // Show summary of what will be uploaded
    const yearsFound = new Set(validFiles.map(f => f.year).filter(y => y !== null))
    const yearSummary = yearsFound.size > 0
      ? `\n\nYears detected: ${Array.from(yearsFound).sort().join(', ')}`
      : '\n\nNo years detected in folder structure'

    if (!confirm(`Upload ${validFiles.length} contract files from folder?${yearSummary}\n\nFiles will be uploaded in batches of 10.`)) {
      event.target.value = ''
      return
    }

    console.log(`📁 Folder upload: ${validFiles.length} files, years: ${Array.from(yearsFound).join(', ') || 'none'}`)

    // Use the same upload logic but just with file objects
    await processFileUpload(validFiles.map(f => f.file), event.target)
  }

  const detectDuplicates = async () => {
    setDetectingDuplicates(true)
    setDuplicateDetectionResults(null)

    try {
      const response = await fetchWithAuth('/api/tprm/contracts/detect-duplicates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      })

      // fetchWithAuth handles 401 redirects automatically, so we don't need to check here

      if (!response.ok) {
        throw new Error('Failed to detect duplicates')
      }

      const results = await response.json()
      setDuplicateDetectionResults(results)

      // Reload contracts to show updated duplicate flags
      await loadContracts()

      addToast({
        id: Date.now().toString(),
        type: 'success',
        title: 'Duplicate Detection Complete',
        message: `Found ${results.duplicatesFound} duplicate contracts`
      })
    } catch (error) {
      console.error('Duplicate detection error:', error)
      addToast({
        id: Date.now().toString(),
        type: 'error',
        title: 'Detection Failed',
        message: error instanceof Error ? error.message : 'Failed to detect duplicates'
      })
    } finally {
      setDetectingDuplicates(false)
    }
  }

  const unmarkDuplicate = async (contractId: string) => {
    try {
      const response = await fetch(`/api/tprm/contracts/${contractId}`, {
        method: 'PUT',
        body: JSON.stringify({
          isDuplicate: false,
          duplicateOf: null,
          markedDuplicateAt: null,
          markedDuplicateBy: null
        }),
        ...createFetchOptions({
          headers: {
            'Content-Type': 'application/json'
          }
        })
      })

      if (!response.ok) {
        throw new Error('Failed to unmark duplicate')
      }

      // Reload contracts
      await loadContracts()

      addToast({
        id: Date.now().toString(),
        type: 'success',
        title: 'Restored Contract',
        message: 'Contract unmarked as duplicate'
      })
    } catch (error) {
      console.error('Unmark duplicate error:', error)
      addToast({
        id: Date.now().toString(),
        type: 'error',
        title: 'Failed to Restore',
        message: error instanceof Error ? error.message : 'Failed to unmark duplicate'
      })
    }
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    await processFileUpload(Array.from(files), event.target)
  }

  const processFileUpload = async (fileArray: File[], inputElement: HTMLInputElement) => {
    try {
      setUploadingFiles(true)
      setUploadProgress(null)

      console.log(`📦 Uploading ${fileArray.length} files one at a time...`)
      console.log(`📝 Files will be uploaded with status 'pending'. Background processor will automatically extract data.`)

      const allUploaded: any[] = []
      const allErrors: any[] = []
      const allSkipped: any[] = []

      // Upload files one at a time sequentially
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i]

        setUploadProgress({
          current: i + 1,
          total: fileArray.length
        })

        console.log(`  📤 Uploading ${i + 1}/${fileArray.length}: ${file.name}`)

        const formData = new FormData()
        formData.append('files', file) // Single file
        formData.append('autoOnboard', 'true')
        formData.append('skipDuplicateCheck', 'true') // Skip for bulk uploads (performance)

        try {
          const response = await fetch(cacheBust('/api/tprm/contracts/upload'), createFetchOptions({
            method: 'POST',
            body: formData
          }))

          if (!response.ok) {
            let errorDetails = `Upload failed (${response.status})`
            try {
              const errorData = await response.json()
              console.error(`  ❌ Server error for ${file.name}:`, errorData)
              errorDetails = errorData.error || errorData.message || errorDetails
            } catch (e) {
              console.error(`  ❌ Could not parse error response`)
            }
            throw new Error(errorDetails)
          }

          const result = await response.json()
          allUploaded.push(...(result.uploaded || []))
          allErrors.push(...(result.errors || []))
          allSkipped.push(...(result.skipped || []))

          console.log(`  ✅ ${file.name} uploaded`)
        } catch (err) {
          console.error(`  ❌ Failed to upload ${file.name}:`, err)
          allErrors.push({
            fileName: file.name,
            error: err instanceof Error ? err.message : 'Unknown error'
          })
        }

        // Small delay to avoid overwhelming the server
        if (i < fileArray.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100)) // 100ms between files
        }
      }

      setUploadProgress(null)

      console.log(`✅ Upload complete: ${allUploaded.length} uploaded, ${allSkipped.length} skipped (duplicates), ${allErrors.length} failed`)

      // Build result message
      let message = `Successfully uploaded ${allUploaded.length} contract${allUploaded.length !== 1 ? 's' : ''}`

      if (allSkipped.length > 0) {
        message += `\n\nSkipped ${allSkipped.length} duplicate${allSkipped.length !== 1 ? 's' : ''}:`
        const skippedSummary = allSkipped.slice(0, 5).map((s: any) => `- ${s.fileName} (matches existing contract)`).join('\n')
        const moreSkipped = allSkipped.length > 5 ? `\n... and ${allSkipped.length - 5} more duplicates` : ''
        message += `\n${skippedSummary}${moreSkipped}`
      }

      if (allErrors.length > 0) {
        message += `\n\n${allErrors.length} file${allErrors.length !== 1 ? 's' : ''} failed:`
        const errorSummary = allErrors.slice(0, 5).map((e: any) => `- ${e.fileName}: ${e.error}`).join('\n')
        const moreErrors = allErrors.length > 5 ? `\n... and ${allErrors.length - 5} more errors` : ''
        message += `\n${errorSummary}${moreErrors}`
      }

      if (allUploaded.length > 0) {
        message += `\n\nContracts uploaded successfully! Background processor will automatically extract data.`
      }

      alert(message)

      // Background processor will automatically process pending contracts
      console.log(`📦 Upload complete. ${allUploaded.length} contracts pending extraction by background processor.`)

      // Reload contracts
      await loadContracts()

      // Reset file input
      inputElement.value = ''
    } catch (err) {
      console.error('Upload error:', err)
      alert('Failed to upload contracts: ' + (err instanceof Error ? err.message : 'Unknown error'))
    } finally {
      setUploadingFiles(false)
      setUploadProgress(null)
    }
  }

  const handleDeleteContract = async (contractId: string) => {
    if (!confirm('Are you sure you want to delete this contract?')) {
      return
    }

    try {
      const response = await fetch(cacheBust(`/api/tprm/contracts/${contractId}`), createFetchOptions({
        method: 'DELETE'
      }))

      if (!response.ok) {
        throw new Error('Failed to delete contract')
      }

      alert('Contract deleted successfully')
      await loadContracts()
    } catch (err) {
      console.error('Delete error:', err)
      alert('Failed to delete contract')
    }
  }

  const loadRenewalSettings = async () => {
    try {
      const url = cacheBust('/api/tprm/contracts/settings')
      console.log('[ContractsManagement] 📡 Loading settings from:', url)
      const response = await fetch(url, createFetchOptions())
      console.log('[ContractsManagement] 📡 Settings response status:', response.status, response.statusText)
      if (response.ok) {
        const settings = await response.json()
        console.log('📥 [loadRenewalSettings] Loaded from backend:', JSON.stringify(settings, null, 2))
        setRenewalSettings(settings)
        const extractedSettings = {
          employeeCount: settings.employeeCount || 1,
          employeeCountByYear: settings.employeeCountByYear,
          defaultCurrency: settings.defaultCurrency || 'USD'
        }
        console.log('📥 [loadRenewalSettings] Setting contractSettings to:', JSON.stringify(extractedSettings, null, 2))
        setContractSettings(extractedSettings)
      } else {
        console.error('[ContractsManagement] ❌ Settings failed:', response.status, await response.text().catch(() => 'no body'))
      }
    } catch (err) {
      console.error('Failed to load renewal settings:', err)
    }
  }

  const saveContractSettings = async () => {
    if (!contractSettings) return

    setSavingSettings(true)
    setSettingsSaved(false)

    try {
      console.log('💾 [saveContractSettings] Saving:', JSON.stringify(contractSettings, null, 2))
      const response = await fetch('/api/tprm/contracts/settings', createFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contractSettings)
      }))

      if (response.ok) {
        const savedData = await response.json()
        console.log('💾 [saveContractSettings] Saved successfully, response:', JSON.stringify(savedData, null, 2))

        // Use fresh data from POST response instead of reloading (which gets stale cached data)
        console.log('✅ [saveContractSettings] Updating local state from POST response (bypassing cache)')
        setRenewalSettings(savedData)
        setContractSettings({
          employeeCount: savedData.employeeCount || 1,
          employeeCountByYear: savedData.employeeCountByYear,
          defaultCurrency: savedData.defaultCurrency || 'USD'
        })

        setSettingsSaved(true)
        setTimeout(() => setSettingsSaved(false), 3000)
      } else {
        alert('Failed to save settings')
      }
    } catch (err) {
      console.error('Failed to save contract settings:', err)
      alert('Failed to save settings')
    } finally {
      setSavingSettings(false)
    }
  }

  const handleScheduleNonRenewal = async (contract: ContractDocument) => {
    setNonRenewalContract(contract)
  }

  const handleSendNonRenewal = async (contractId: string) => {
    if (!confirm('Send non-renewal notice now? This will immediately notify the vendor.')) {
      return
    }

    try {
      const response = await fetch(cacheBust(`/api/tprm/contracts/${contractId}/send-non-renewal`), createFetchOptions({
        method: 'POST'
      }))

      if (!response.ok) {
        throw new Error('Failed to send non-renewal notice')
      }

      alert('Non-renewal notice sent successfully!')
      await loadContracts()
    } catch (err) {
      console.error('Non-renewal error:', err)
      alert('Failed to send non-renewal notice: ' + (err instanceof Error ? err.message : 'Unknown error'))
    }
  }

  const handleCancelNonRenewal = async (contractId: string) => {
    if (!confirm('Cancel scheduled non-renewal?')) {
      return
    }

    try {
      const response = await fetch(cacheBust(`/api/tprm/contracts/${contractId}/cancel-non-renewal`), createFetchOptions({
        method: 'DELETE'
      }))

      if (!response.ok) {
        throw new Error('Failed to cancel non-renewal')
      }

      alert('Non-renewal cancelled successfully!')
      await loadContracts()
    } catch (err) {
      console.error('Cancel non-renewal error:', err)
      alert('Failed to cancel non-renewal: ' + (err instanceof Error ? err.message : 'Unknown error'))
    }
  }

  const handleConfirmNonRenewal = async (reason?: string) => {
    if (!nonRenewalContract) return

    try {
      const response = await fetch(cacheBust(`/api/tprm/contracts/${nonRenewalContract.id}/schedule-non-renewal`), createFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      }))

      if (!response.ok) {
        throw new Error('Failed to schedule non-renewal')
      }

      const result = await response.json()

      if (result.requiresApproval) {
        alert(`Non-renewal scheduled pending approval from: ${result.approvers.join(', ')}`)
      } else {
        alert('Non-renewal scheduled successfully!')
      }

      setNonRenewalContract(null)
      await loadContracts()
    } catch (err) {
      console.error('Schedule non-renewal error:', err)
      alert('Failed to schedule non-renewal: ' + (err instanceof Error ? err.message : 'Unknown error'))
    }
  }

  const getRenewalStatusBadge = (status?: string) => {
    const badges = {
      'active': 'bg-green-100 text-green-800',
      'expiring_soon': 'bg-yellow-100 text-yellow-800',
      'expired': 'bg-red-100 text-red-800',
      'non_renew_scheduled': 'bg-orange-100 text-orange-800',
      'renewed': 'bg-blue-100 text-blue-800',
      'terminated': 'bg-gray-100 text-gray-800'
    }
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800'
  }

  const getDaysUntilRenewal = (renewalDate?: string) => {
    if (!renewalDate) return null

    const today = new Date()
    const renewal = new Date(renewalDate)
    const diffTime = renewal.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return diffDays
  }

  const loadAnalytics = async () => {
    try {
      const url = cacheBust('/api/tprm/contracts/analytics')
      console.log('[ContractsManagement] 📡 Loading analytics from:', url)
      const response = await fetch(url, createFetchOptions())
      console.log('[ContractsManagement] 📡 Analytics response status:', response.status, response.statusText)
      if (response.ok) {
        const data = await response.json()
        setAnalytics(data)
      } else {
        console.error('[ContractsManagement] ❌ Analytics failed:', response.status, await response.text().catch(() => 'no body'))
      }
    } catch (err) {
      console.error('Failed to load analytics:', err)
    }
  }

  const handleArchiveContract = async (contractId: string, reason?: string) => {
    if (!confirm('Archive this contract? It will be moved to the archived contracts view.')) {
      return
    }

    try {
      const response = await fetch(cacheBust(`/api/tprm/contracts/${contractId}/archive`), createFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      }))

      if (!response.ok) {
        // Try to get the error message from the response body
        let errorMessage = 'Failed to archive contract'
        try {
          const errorData = await response.json()
          if (errorData.error) {
            errorMessage = errorData.error
          }
        } catch {
          // If parsing fails, use default message
        }
        throw new Error(errorMessage)
      }

      alert('Contract archived successfully!')
      await loadContracts()
      // Analytics will be recalculated when manually triggered
    } catch (err) {
      console.error('Archive error:', err)
      alert('Failed to archive contract: ' + (err instanceof Error ? err.message : 'Unknown error'))
    }
  }

  const handleUnarchiveContract = async (contractId: string) => {
    if (!confirm('Unarchive this contract? It will be moved back to active contracts.')) {
      return
    }

    try {
      const response = await fetch(cacheBust(`/api/tprm/contracts/${contractId}/unarchive`), createFetchOptions({
        method: 'POST'
      }))

      if (!response.ok) {
        // Try to get the error message from the response body
        let errorMessage = 'Failed to unarchive contract'
        try {
          const errorData = await response.json()
          if (errorData.error) {
            errorMessage = errorData.error
          }
        } catch {
          // If parsing fails, use default message
        }
        throw new Error(errorMessage)
      }

      alert('Contract unarchived successfully!')
      await loadContracts()
      // Analytics will be recalculated when manually triggered
    } catch (err) {
      console.error('Unarchive error:', err)
      alert('Failed to unarchive contract: ' + (err instanceof Error ? err.message : 'Unknown error'))
    }
  }

  const handleMarkDuplicate = async (contractId: string, duplicateOf?: string) => {
    if (!confirm('Mark this contract as a duplicate? It will be moved to the duplicates view.')) {
      return
    }

    try {
      const response = await fetch(cacheBust(`/api/tprm/contracts/${contractId}/duplicate`), createFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDuplicate: true, duplicateOf })
      }))

      if (!response.ok) {
        throw new Error('Failed to mark contract as duplicate')
      }

      alert('Contract marked as duplicate!')
      await loadContracts()
    } catch (err) {
      console.error('Mark duplicate error:', err)
      alert('Failed to mark contract as duplicate: ' + (err instanceof Error ? err.message : 'Unknown error'))
    }
  }

  const handleUnmarkDuplicate = async (contractId: string) => {
    if (!confirm('Unmark this contract as duplicate? It will be moved back to active contracts.')) {
      return
    }

    try {
      const response = await fetch(cacheBust(`/api/tprm/contracts/${contractId}/duplicate`), createFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDuplicate: false })
      }))

      if (!response.ok) {
        throw new Error('Failed to unmark contract as duplicate')
      }

      alert('Contract unmarked as duplicate!')
      await loadContracts()
    } catch (err) {
      console.error('Unmark duplicate error:', err)
      alert('Failed to unmark contract as duplicate: ' + (err instanceof Error ? err.message : 'Unknown error'))
    }
  }

  const loadInsights = async () => {
    try {
      const url = cacheBust('/api/tprm/contracts/insights?status=new')
      console.log('[ContractsManagement] 📡 Loading insights from:', url)
      const response = await fetch(url, createFetchOptions())
      console.log('[ContractsManagement] 📡 Insights response status:', response.status, response.statusText)
      if (response.ok) {
        const data = await response.json()
        setInsights(data)
      } else {
        console.error('[ContractsManagement] ❌ Insights failed:', response.status, await response.text().catch(() => 'no body'))
      }
    } catch (err) {
      console.error('Failed to load insights:', err)
    }
  }

  const handleOpenInsightsModal = () => {
    // Pre-select only PROCESSED contracts (only those can be analyzed)
    const processedIds = new Set(
      contracts
        .filter(c => c.extractionStatus === 'processed' || c.extractionStatus === 'completed')
        .map(c => c.id)
    )
    setSelectedContractIds(processedIds)
    setShowInsightsModal(true)
  }

  const handleAnalyzeAllContracts = async () => {
    const processedCount = extractionCounts.processed

    if (processedCount === 0) {
      alert('No processed contracts available for analysis. Please wait for contracts to be processed.')
      return
    }

    if (!confirm(`Analyze all ${processedCount} processed contracts? This may take a few moments.`)) {
      return
    }

    try {
      setGeneratingInsights(true)

      // Send empty contractIds array with processedOnly flag to analyze all processed contracts
      // Pass pre-loaded contracts to avoid backend timeout
      const processedContracts = contracts.filter(c =>
        c.extractionStatus === 'processed' || c.extractionStatus === 'completed'
      )

      const response = await fetch(cacheBust('/api/tprm/contracts/insights/generate'), createFetchOptions({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contractIds: [], // Empty array = analyze all
          processedOnly: true, // Only analyze processed contracts
          contracts: processedContracts
        })
      }))

      if (!response.ok) {
        throw new Error('Failed to generate insights')
      }

      const result = await response.json()
      alert(`Generated ${result.insightsGenerated} new insights from ${processedCount} processed contracts!`)
      await loadInsights()
    } catch (err) {
      console.error('Analyze all contracts error:', err)
      alert('Failed to generate insights: ' + (err instanceof Error ? err.message : 'Unknown error'))
    } finally {
      setGeneratingInsights(false)
    }
  }

  const handleToggleContract = (contractId: string) => {
    const newSelection = new Set(selectedContractIds)
    if (newSelection.has(contractId)) {
      newSelection.delete(contractId)
    } else {
      newSelection.add(contractId)
    }
    setSelectedContractIds(newSelection)
  }

  const handleToggleAllContracts = () => {
    // Apply the same filtering logic as the render method to get visible contracts
    let filteredContracts = contracts

    // Exclude archived and duplicates by default (matches render logic)
    if (filter !== 'archived') {
      filteredContracts = filteredContracts.filter(c => !c.isArchived)
    }
    if (activeTab !== 'duplicates') {
      filteredContracts = filteredContracts.filter(c => !c.isDuplicate)
    }

    // First apply main filter (expiring)
    if (filter === 'expiring') {
      filteredContracts = filteredContracts.filter(c => {
        if (!c.expirationDate) return false
        const expirationDate = new Date(c.expirationDate)
        const daysUntilExpiration = Math.floor((expirationDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
        // Show contracts expiring within 90 days and not already expired
        return daysUntilExpiration >= 0 && daysUntilExpiration <= 90
      })
    }

    // Then apply extraction status filtering
    if (extractionStatusFilter !== 'all') {
      filteredContracts = filteredContracts.filter(c => {
        if (extractionStatusFilter === 'processed') {
          // Handle both 'processed' and old 'completed' status during migration
          return c.extractionStatus === 'processed' || c.extractionStatus === 'completed'
        }
        return c.extractionStatus === extractionStatusFilter
      })
    }

    // Apply renewal year filter (exclude past years)
    if (renewalYearFilter !== 'all') {
      const filterYear = parseInt(renewalYearFilter)
      filteredContracts = filteredContracts.filter(c => {
        if (!c.renewalDate) return false // Exclude contracts without renewal date
        const renewalYear = new Date(c.renewalDate).getFullYear()
        return renewalYear >= filterYear // Show contracts with renewal year >= selected year
      })
    }

    // Apply document type filter
    if (documentTypeFilter !== 'all') {
      filteredContracts = filteredContracts.filter(c => {
        const docType = safeExtractValue<string>(c.documentType)
        if (documentTypeFilter === 'EMPTY') {
          return !docType || docType === ''
        }
        return docType === documentTypeFilter
      })
    }

    // Apply vendor search filter
    if (vendorSearchTerm.trim()) {
      filteredContracts = filteredContracts.filter(c => {
        const vendorName = safeExtractValue<string>(c.vendorName) || ''
        return vendorName.toLowerCase().includes(vendorSearchTerm.toLowerCase())
      })
    }

    // Apply date filter - from current date onwards
    if (fromCurrentDateOnly) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      filteredContracts = filteredContracts.filter(c => {
        if (c.renewalDate) {
          const renewalDate = new Date(c.renewalDate)
          if (renewalDate >= today) return true
        }
        if (c.expirationDate) {
          const expirationDate = new Date(c.expirationDate)
          if (expirationDate >= today) return true
        }
        const effectiveDate = c.extractedMetadata?.dates?.effectiveDate?.value
        if (effectiveDate) {
          const contractDate = new Date(effectiveDate)
          if (contractDate >= today) return true
        }
        return false
      })
    }

    // Apply seats filter - only contracts with seat counts
    if (hasSeatsOnly) {
      filteredContracts = filteredContracts.filter(c => {
        const seats = safeExtractValue<number>(c.numberOfLicenses)
        return seats !== undefined && seats !== null && seats > 0
      })
    }

    // Apply seat count comparison filter
    if (seatCountOperator !== 'all' && seatCountValue > 0) {
      filteredContracts = filteredContracts.filter(c => {
        const seats = safeExtractValue<number>(c.numberOfLicenses)
        if (seats === undefined || seats === null) return false

        switch (seatCountOperator) {
          case '>':
            return seats > seatCountValue
          case '<':
            return seats < seatCountValue
          case '=':
            return seats === seatCountValue
          default:
            return true
        }
      })
    }

    // Toggle selection for the filtered contracts
    if (selectedContractIds.size === filteredContracts.length && filteredContracts.length > 0) {
      // Deselect all
      setSelectedContractIds(new Set())
    } else {
      // Select all filtered contracts
      const allIds = new Set(filteredContracts.map(c => c.id))
      setSelectedContractIds(allIds)
    }
  }

  const handleBulkUpdate = async () => {
    if (selectedContractIds.size === 0) {
      addToast({
        type: 'error',
        title: 'No contracts selected',
        message: 'Please select at least one contract to update'
      })
      return
    }

    // Build updates object - only include fields that have been set
    const updates: any = {}
    if (bulkEditDocumentType) updates.documentType = bulkEditDocumentType
    if (bulkEditITSpend !== null) updates.isITSpend = bulkEditITSpend
    if (bulkEditArchive !== null) updates.isArchived = bulkEditArchive

    if (Object.keys(updates).length === 0) {
      addToast({
        type: 'error',
        title: 'No changes specified',
        message: 'Please select at least one field to update'
      })
      return
    }

    try {
      setBulkUpdating(true)

      const response = await fetch(cacheBust('/api/tprm/contracts/bulk-update'), createFetchOptions({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contractIds: Array.from(selectedContractIds),
          updates
        })
      }))

      if (!response.ok) {
        throw new Error('Failed to bulk update contracts')
      }

      const result = await response.json()
      console.log('🔄 [Bulk Update] API Response:', result)
      console.log('🔄 [Bulk Update] Updated count:', result.updated)
      console.log('🔄 [Bulk Update] Failed count:', result.failed)

      if (result.success) {
        addToast({
          type: 'success',
          title: 'Bulk update successful',
          message: `Updated ${result.updated} contract(s)${result.failed > 0 ? `, ${result.failed} failed` : ''}`
        })

        // Refresh contracts list to show updates
        await loadContracts()

        // Clear selection and reset bulk edit form
        setSelectedContractIds(new Set())
        setBulkEditDocumentType('')
        setBulkEditITSpend(null)
        setBulkEditArchive(null)
        setShowBulkEditToolbar(false)
      } else {
        throw new Error(result.error || 'Unknown error')
      }
    } catch (err) {
      console.error('Bulk update error:', err)
      addToast({
        type: 'error',
        title: 'Bulk update failed',
        message: err instanceof Error ? err.message : 'Unknown error'
      })
    } finally {
      setBulkUpdating(false)
    }
  }

  const handleGenerateInsights = async () => {
    if (selectedContractIds.size === 0) {
      alert('Please select at least one contract to analyze')
      return
    }

    try {
      setGeneratingInsights(true)
      setShowInsightsModal(false)

      // Pass pre-loaded contracts to avoid backend timeout loading 851 contracts from blobs
      const selectedContracts = contracts.filter(c => selectedContractIds.has(c.id))

      const response = await fetch(cacheBust('/api/tprm/contracts/insights/generate'), createFetchOptions({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contractIds: Array.from(selectedContractIds),
          contracts: selectedContracts
        })
      }))

      if (!response.ok) {
        throw new Error('Failed to generate insights')
      }

      const result = await response.json()
      alert(`Generated ${result.insightsGenerated} new insights from ${selectedContractIds.size} contracts!`)
      await loadInsights()
    } catch (err) {
      console.error('Generate insights error:', err)
      alert('Failed to generate insights: ' + (err instanceof Error ? err.message : 'Unknown error'))
    } finally {
      setGeneratingInsights(false)
    }
  }

  const handleAcknowledgeInsight = async (insightId: string) => {
    try {
      const response = await fetch(cacheBust(`/api/tprm/contracts/insights/${insightId}/acknowledge`), createFetchOptions({
        method: 'POST'
      }))

      if (!response.ok) {
        throw new Error('Failed to acknowledge insight')
      }

      await loadInsights()
    } catch (err) {
      console.error('Acknowledge insight error:', err)
      alert('Failed to acknowledge insight')
    }
  }

  const handleDismissInsight = async (insightId: string) => {
    if (!confirm('Dismiss this insight? It will be hidden from the list.')) {
      return
    }

    try {
      const response = await fetch(cacheBust(`/api/tprm/contracts/insights/${insightId}`), createFetchOptions({
        method: 'DELETE'
      }))

      if (!response.ok) {
        throw new Error('Failed to dismiss insight')
      }

      await loadInsights()
    } catch (err) {
      console.error('Dismiss insight error:', err)
      alert('Failed to dismiss insight')
    }
  }

  const getSeverityColor = (severity: string) => {
    const colors = {
      critical: 'bg-red-100 border-red-300 text-red-800',
      alert: 'bg-orange-100 border-orange-300 text-orange-800',
      warning: 'bg-yellow-100 border-yellow-300 text-yellow-800',
      info: 'bg-blue-100 border-blue-300 text-blue-800'
    }
    return colors[severity as keyof typeof colors] || colors.info
  }

  const getSeverityIcon = (severity: string) => {
    if (severity === 'critical' || severity === 'alert') {
      return <AlertCircle className="h-5 w-5" />
    }
    return <Lightbulb className="h-5 w-5" />
  }

  const getStatusIcon = (contract: ContractDocument) => {
    if (contract.extractionStatus === 'completed') {
      return <CheckCircle className="h-5 w-5 text-green-500" />
    } else if (contract.extractionStatus === 'failed') {
      return <AlertCircle className="h-5 w-5 text-red-500" />
    } else {
      return <Clock className="h-5 w-5 text-yellow-500" />
    }
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    }
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800'
  }

  const formatCurrency = (value?: number, currency?: string) => {
    if (!value) return 'N/A'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(value)
  }

  const formatDate = (date?: string) => {
    if (!date) return 'N/A'
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Filter and sort customer contracts
  const getFilteredAndSortedCustomerContracts = () => {
    let filtered = [...customerContracts]

    // Apply search filter
    if (customerSearchQuery) {
      const query = customerSearchQuery.toLowerCase()
      filtered = filtered.filter(contract =>
        contract.customerName.toLowerCase().includes(query) ||
        contract.contractValue.toString().includes(query) ||
        (contract.notes && contract.notes.toLowerCase().includes(query))
      )
    }

    // Apply status filter
    if (customerStatusFilter !== 'all') {
      filtered = filtered.filter(contract => contract.status === customerStatusFilter)
    }

    // Apply sorting
    if (customerSortColumn) {
      filtered.sort((a, b) => {
        let aVal, bVal

        switch (customerSortColumn) {
          case 'customerName':
            aVal = a.customerName.toLowerCase()
            bVal = b.customerName.toLowerCase()
            break
          case 'contractValue':
            aVal = a.contractValue
            bVal = b.contractValue
            break
          case 'startDate':
            aVal = new Date(a.startDate).getTime()
            bVal = new Date(b.startDate).getTime()
            break
          case 'renewalDate':
            aVal = new Date(a.renewalDate).getTime()
            bVal = new Date(b.renewalDate).getTime()
            break
          default:
            return 0
        }

        if (aVal < bVal) return customerSortDirection === 'asc' ? -1 : 1
        if (aVal > bVal) return customerSortDirection === 'asc' ? 1 : -1
        return 0
      })
    }

    return filtered
  }

  // Export customer contracts to CSV
  const exportCustomerContractsToCSV = () => {
    const contractsToExport = getFilteredAndSortedCustomerContracts()

    // Prepare CSV headers
    const headers = [
      'Customer Name',
      'ARR',
      'Currency',
      'Start Date',
      'Renewal Date',
      'Status',
      'Notes'
    ]

    // Prepare CSV rows
    const rows = contractsToExport.map(contract => [
      contract.customerName,
      contract.contractValue.toString(),
      contract.currency || 'USD',
      new Date(contract.startDate).toLocaleDateString(),
      new Date(contract.renewalDate).toLocaleDateString(),
      contract.status,
      (contract.notes || '').replace(/"/g, '""') // Escape quotes
    ])

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `customer-contracts-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Handle customer sort column click
  const handleCustomerSort = (column: 'customerName' | 'contractValue' | 'startDate' | 'renewalDate') => {
    if (customerSortColumn === column) {
      // Toggle direction
      setCustomerSortDirection(customerSortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to ascending
      setCustomerSortColumn(column)
      setCustomerSortDirection('asc')
    }
  }

  const exportToCSV = () => {
    // Prepare CSV headers
    const headers = [
      'Status',
      'Vendor Name',
      'File Name',
      'Contract Date',
      'Value',
      'Currency',
      'IT Spend',
      'Seats',
      'Renewal Date',
      'Expiration Date',
      'Uploaded At',
      'Uploaded By',
      'Auto-Renewal',
      'Renewal Status',
      'Archived',
      'Archive Year',
      'ID'
    ]

    // Convert contracts to CSV rows
    const rows = contracts.map(contract => [
      contract.extractionStatus || '',
      safeExtractValue<string>(contract.vendorName) || '',
      contract.fileName || '',
      contract.extractedMetadata?.dates?.effectiveDate?.value || '',
      safeExtractValue<number>(contract.contractValue)?.toString() || '',
      safeExtractValue<string>(contract.contractCurrency) || 'USD',
      contract.isITSpend ? 'Yes' : 'No',
      safeExtractValue<number>(contract.numberOfLicenses)?.toString() || '',
      contract.renewalDate || '',
      contract.expirationDate || '',
      contract.uploadedAt || '',
      contract.uploadedBy || '',
      contract.autoRenew ? 'Yes' : 'No',
      contract.renewalStatus || '',
      contract.isArchived ? 'Yes' : 'No',
      contract.archiveYear?.toString() || '',
      contract.id || ''
    ])

    // Escape CSV values (handle commas, quotes, newlines)
    const escapeCSV = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`
      }
      return value
    }

    // Build CSV content
    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n')

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', `contracts_export_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Contract Management</h1>
          <p className="text-gray-600">
            Upload and manage vendor contracts with AI-powered extraction and TPRM integration
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={exportToCSV}
            disabled={contracts.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Export all contracts to CSV"
          >
            <Download className="h-5 w-5" />
            Export CSV
          </button>
          <button
            onClick={() => setShowRenewalSettings(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Settings className="h-5 w-5" />
            Renewal Settings
          </button>
        </div>
      </div>

      {/* Persistent Reprocessing Progress Banner */}
      {activeJob && activeJob.status === 'running' && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">
                  Re-processing contract {activeJob.processedCount} of {activeJob.totalContracts}
                </p>
                <div className="flex items-center gap-3 mt-1">
                  <div className="flex-1 bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(activeJob.processedCount / activeJob.totalContracts) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-blue-700 font-medium">
                    {Math.round((activeJob.processedCount / activeJob.totalContracts) * 100)}%
                  </span>
                </div>
                {activeJob.currentContract && (
                  <p className="text-xs text-blue-600 mt-1">
                    Currently processing: {activeJob.currentContract}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs Navigation - Only show if not controlled by parent */}
      {!initialTab && (
        <div className="mb-6 border-b border-gray-200" data-tour="contracts-tabs">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'settings'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-tour-subtab="contracts-settings"
            >
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Settings
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Customers Tab Content */}
      {activeTab === 'customers' && (
        <div className="p-6 space-y-6" data-tour="contracts-customers-panel">
          {/* Analytics Summary Cards */}
          {customerAnalytics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total ARR</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(customerAnalytics.totalARR, 'USD')}
                    </p>
                  </div>
                  <DollarSign className="h-10 w-10 text-green-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Active Customers</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {customerAnalytics.activeCustomers}
                    </p>
                  </div>
                  <Users className="h-10 w-10 text-blue-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Churned Customers</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {customerAnalytics.churnedCustomers}
                    </p>
                  </div>
                  <XCircle className="h-10 w-10 text-red-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Avg Contract Value</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(customerAnalytics.avgContractValue, 'USD')}
                    </p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-purple-500" />
                </div>
              </div>
            </div>
          )}

          {/* Renewal Alerts Widget */}
          {customerAnalytics && customerAnalytics.renewalAlerts && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <Bell className="h-5 w-5 text-orange-500" />
                <h3 className="text-lg font-semibold text-gray-900">Upcoming Renewals</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* 30 Days */}
                <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-red-900">Next 30 Days</h4>
                    <span className="text-sm font-bold text-red-900">
                      {customerAnalytics.renewalAlerts[30]?.length || 0}
                    </span>
                  </div>
                  {customerAnalytics.renewalAlerts[30]?.length > 0 ? (
                    <div className="space-y-2 mt-3">
                      {customerAnalytics.renewalAlerts[30].slice(0, 3).map(contract => (
                        <div key={contract.id} className="text-sm">
                          <div className="font-medium text-gray-900">{contract.customerName}</div>
                          <div className="text-gray-600">
                            {formatCurrency(contract.contractValue, contract.currency)} - {new Date(contract.renewalDate).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                      {customerAnalytics.renewalAlerts[30].length > 3 && (
                        <div className="text-sm text-red-700 font-medium">
                          +{customerAnalytics.renewalAlerts[30].length - 3} more
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-red-700 mt-2">No renewals in this period</p>
                  )}
                </div>

                {/* 60 Days */}
                <div className="border-l-4 border-orange-500 bg-orange-50 p-4 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-orange-900">Next 60 Days</h4>
                    <span className="text-sm font-bold text-orange-900">
                      {customerAnalytics.renewalAlerts[60]?.length || 0}
                    </span>
                  </div>
                  {customerAnalytics.renewalAlerts[60]?.length > 0 ? (
                    <div className="space-y-2 mt-3">
                      {customerAnalytics.renewalAlerts[60].slice(0, 3).map(contract => (
                        <div key={contract.id} className="text-sm">
                          <div className="font-medium text-gray-900">{contract.customerName}</div>
                          <div className="text-gray-600">
                            {formatCurrency(contract.contractValue, contract.currency)} - {new Date(contract.renewalDate).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                      {customerAnalytics.renewalAlerts[60].length > 3 && (
                        <div className="text-sm text-orange-700 font-medium">
                          +{customerAnalytics.renewalAlerts[60].length - 3} more
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-orange-700 mt-2">No renewals in this period</p>
                  )}
                </div>

                {/* 90 Days */}
                <div className="border-l-4 border-yellow-500 bg-yellow-50 p-4 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-yellow-900">Next 90 Days</h4>
                    <span className="text-sm font-bold text-yellow-900">
                      {customerAnalytics.renewalAlerts[90]?.length || 0}
                    </span>
                  </div>
                  {customerAnalytics.renewalAlerts[90]?.length > 0 ? (
                    <div className="space-y-2 mt-3">
                      {customerAnalytics.renewalAlerts[90].slice(0, 3).map(contract => (
                        <div key={contract.id} className="text-sm">
                          <div className="font-medium text-gray-900">{contract.customerName}</div>
                          <div className="text-gray-600">
                            {formatCurrency(contract.contractValue, contract.currency)} - {new Date(contract.renewalDate).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                      {customerAnalytics.renewalAlerts[90].length > 3 && (
                        <div className="text-sm text-yellow-700 font-medium">
                          +{customerAnalytics.renewalAlerts[90].length - 3} more
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-yellow-700 mt-2">No renewals in this period</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Search, Filter, and Action Buttons */}
          <div className="space-y-4">
            {/* Search and Status Filter */}
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by customer name, value, or notes..."
                  value={customerSearchQuery}
                  onChange={(e) => setCustomerSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={customerStatusFilter}
                onChange={(e) => setCustomerStatusFilter(e.target.value as 'all' | 'active' | 'churned')}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="churned">Churned</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddCustomerModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-5 w-5" />
                Add Contract
              </button>
              <button
                onClick={() => setShowImportCustomerCSV(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Upload className="h-5 w-5" />
                Import CSV
              </button>
              <button
                onClick={exportCustomerContractsToCSV}
                disabled={customerContracts.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-5 w-5" />
                Export to CSV
              </button>
            </div>
          </div>

          {/* Contracts Table */}
          <div className="bg-white rounded-lg shadow">
            {customerContractsLoading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading contracts...</p>
              </div>
            ) : customerContractsError ? (
              <div className="p-8 text-center text-red-600">
                <AlertCircle className="h-12 w-12 mx-auto mb-2" />
                <p>{customerContractsError}</p>
              </div>
            ) : customerContracts.length === 0 ? (
              <div className="p-8 text-center text-gray-600">
                <Building className="h-12 w-12 mx-auto mb-2" />
                <p>No customer contracts yet. Add your first contract to get started!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full md:min-w-[640px] divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        onClick={() => handleCustomerSort('customerName')}
                        className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      >
                        <div className="flex items-center gap-1">
                          Customer
                          {customerSortColumn === 'customerName' && (
                            customerSortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        onClick={() => handleCustomerSort('contractValue')}
                        className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      >
                        <div className="flex items-center gap-1">
                          ARR
                          {customerSortColumn === 'contractValue' && (
                            customerSortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        onClick={() => handleCustomerSort('startDate')}
                        className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none hidden md:table-cell"
                      >
                        <div className="flex items-center gap-1">
                          Start Date
                          {customerSortColumn === 'startDate' && (
                            customerSortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        onClick={() => handleCustomerSort('renewalDate')}
                        className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      >
                        <div className="flex items-center gap-1">
                          Renewal
                          {customerSortColumn === 'renewalDate' && (
                            customerSortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredAndSortedCustomerContracts().map(contract => (
                      <tr key={contract.id} className="hover:bg-gray-50">
                        <td className="px-3 sm:px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 break-all">{contract.customerName}</div>
                          {contract.notes && (
                            <div className="text-xs sm:text-sm text-gray-500">{contract.notes.substring(0, 50)}{contract.notes.length > 50 && '...'}</div>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(contract.contractValue, contract.currency)}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                          {new Date(contract.startDate).toLocaleDateString()}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(contract.renewalDate).toLocaleDateString()}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            contract.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {contract.status}
                          </span>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => setSelectedCustomerContract(contract)}
                            className="text-blue-600 hover:text-blue-900 mr-2 sm:mr-4 p-1 touch-manipulation"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteCustomerContract(contract.id, contract.customerName)}
                            className="text-red-600 hover:text-red-900 p-1 touch-manipulation"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Add/Edit Contract Modal */}
          {(showAddCustomerModal || selectedCustomerContract) && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">
                      {selectedCustomerContract ? 'Edit Contract' : 'Add Contract'}
                    </h2>
                    <button
                      onClick={() => {
                        setShowAddCustomerModal(false)
                        setSelectedCustomerContract(null)
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault()
                      const formData = new FormData(e.currentTarget)
                      const data = {
                        customerName: formData.get('customerName') as string,
                        contractValue: parseFloat(formData.get('contractValue') as string),
                        currency: formData.get('currency') as string || 'USD',
                        startDate: formData.get('startDate') as string,
                        renewalDate: formData.get('renewalDate') as string,
                        status: formData.get('status') as 'active' | 'churned',
                        notes: formData.get('notes') as string || ''
                      }

                      if (selectedCustomerContract) {
                        handleUpdateCustomerContract({ ...selectedCustomerContract, ...data })
                      } else {
                        handleCreateCustomerContract(data)
                      }
                    }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Customer Name *
                      </label>
                      <input
                        type="text"
                        name="customerName"
                        defaultValue={selectedCustomerContract?.customerName || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Acme Corp"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contract Value (ARR) *
                        </label>
                        <input
                          type="number"
                          name="contractValue"
                          defaultValue={selectedCustomerContract?.contractValue || ''}
                          required
                          step="0.01"
                          min="0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="50000"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Currency
                        </label>
                        <input
                          type="text"
                          name="currency"
                          defaultValue={selectedCustomerContract?.currency || 'USD'}
                          maxLength={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="USD"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Date *
                        </label>
                        <input
                          type="date"
                          name="startDate"
                          defaultValue={selectedCustomerContract?.startDate.split('T')[0] || ''}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Renewal Date *
                        </label>
                        <input
                          type="date"
                          name="renewalDate"
                          defaultValue={selectedCustomerContract?.renewalDate.split('T')[0] || ''}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status *
                      </label>
                      <select
                        name="status"
                        defaultValue={selectedCustomerContract?.status || 'active'}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="active">Active</option>
                        <option value="churned">Churned</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes
                      </label>
                      <textarea
                        name="notes"
                        defaultValue={selectedCustomerContract?.notes || ''}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Additional notes about this contract..."
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        {selectedCustomerContract ? 'Update Contract' : 'Create Contract'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddCustomerModal(false)
                          setSelectedCustomerContract(null)
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* CSV Import Modal */}
          {showImportCustomerCSV && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Import Contracts from CSV</h3>
                    <button
                      onClick={() => setShowImportCustomerCSV(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-4">
                        Upload a CSV file with the following columns:
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1 mb-4 list-disc list-inside">
                        <li><strong>customerName</strong> - Customer name (required)</li>
                        <li><strong>contractValue</strong> - ARR in dollars (required)</li>
                        <li><strong>startDate</strong> - Contract start date YYYY-MM-DD (required)</li>
                        <li><strong>renewalDate</strong> - Next renewal date YYYY-MM-DD (required)</li>
                        <li><strong>currency</strong> - Currency code (optional, defaults to USD)</li>
                        <li><strong>status</strong> - "active" or "churned" (optional, defaults to active)</li>
                        <li><strong>notes</strong> - Additional notes (optional)</li>
                      </ul>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select CSV File
                      </label>
                      <input
                        type="file"
                        accept=".csv"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            handleCustomerCSVImport(file)
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                      <button
                        type="button"
                        onClick={() => setShowImportCustomerCSV(false)}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Legacy Analytics - keeping for reference but hidden */}
      {false && activeTab === 'dashboard-old' && (
        <>
          {/* Analytics Summary */}
          {analytics && (
            <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Contracts</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.totalContracts}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.activeContracts} active, {analytics.archivedContracts} archived
                </p>
              </div>
              <FileText className="h-10 w-10 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Spend</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(analytics.totalSpend, 'USD')}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Avg: {formatCurrency(analytics.averageContractValue, 'USD')}
                </p>
              </div>
              <DollarSign className="h-10 w-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Renewals (90d)</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.renewalsNext90Days}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Value: {formatCurrency(analytics.renewalValueNext90Days, 'USD')}
                </p>
              </div>
              <TrendingUp className="h-10 w-10 text-orange-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">TPRM Onboarded</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.onboardedToTprm}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.contractsWithDpa} with DPA
                </p>
              </div>
              <Shield className="h-10 w-10 text-purple-500" />
            </div>
          </div>
        </div>
      )}

      {/* Spend Trends Over Time */}
      {analytics && analytics.spendTrends && analytics.spendTrends.length > 0 && (
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <div className="mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-blue-500" />
              Spend Trends Over Time
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Historical view of total spend and spend per license
            </p>
          </div>

          <div className="space-y-6">
            {/* Total Spend Chart */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Total Monthly Spend</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  data={analytics.spendTrends}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const [year, month] = value.split('-')
                      return `${month}/${year.substring(2)}`
                    }}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    formatter={(value: number) => [`$${value.toLocaleString()}`, 'Total Spend']}
                    labelFormatter={(label) => {
                      const [year, month] = label.split('-')
                      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                      return `${monthNames[parseInt(month) - 1]} ${year}`
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="totalSpend"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    name="Total Spend"
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Spend Per License Chart */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Spend Per License</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  data={analytics.spendTrends}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const [year, month] = value.split('-')
                      return `${month}/${year.substring(2)}`
                    }}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `$${value.toFixed(0)}`}
                  />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      if (name === 'spendPerLicense') {
                        return [`$${value.toFixed(2)}`, 'Spend Per License']
                      }
                      return [value, name]
                    }}
                    labelFormatter={(label) => {
                      const [year, month] = label.split('-')
                      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                      return `${monthNames[parseInt(month) - 1]} ${year}`
                    }}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="spendPerLicense"
                    stroke="#10b981"
                    strokeWidth={2}
                    name="Spend Per License"
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <p className="text-sm text-gray-600">Earliest Data</p>
                <p className="text-lg font-semibold text-gray-900">
                  {(() => {
                    const [year, month] = analytics.spendTrends[0].month.split('-')
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                    return `${monthNames[parseInt(month) - 1]} ${year}`
                  })()}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Latest Data</p>
                <p className="text-lg font-semibold text-gray-900">
                  {(() => {
                    const [year, month] = analytics.spendTrends[analytics.spendTrends.length - 1].month.split('-')
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                    return `${monthNames[parseInt(month) - 1]} ${year}`
                  })()}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Data Points</p>
                <p className="text-lg font-semibold text-gray-900">
                  {analytics.spendTrends.length} months
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archived Contracts by Year */}
      {analytics && analytics.archivedByYear && Object.keys(analytics.archivedByYear).length > 0 && (
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Archive className="h-6 w-6 text-gray-500" />
              Archived Contracts by Year
            </h2>
            <span className="text-sm text-gray-500">
              Total Archived: {analytics.archivedContracts || 0}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {Object.entries(analytics.archivedByYear)
              .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
              .map(([year, count]) => (
                <div key={year} className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                  <p className="text-sm text-gray-600">{year}</p>
                </div>
              ))}
          </div>
        </div>
      )}
        </>
      )}

      {/* Insights Tab Content */}
      {activeTab === 'insights' && (
        <>
          {/* Vendor Risk Management Dashboard - Moved to unified TPRM Dashboard */}
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">📊 Dashboard Moved</h3>
            <p className="text-blue-800">
              The Vendor Risk Management Dashboard has been moved to the unified TPRM Dashboard.
              Navigate to <strong>Knowledge Base → TPRM → Dashboard</strong> to view all TPRM metrics in one place.
            </p>
          </div>

          {/* Insights Section */}
          {insights.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Lightbulb className="h-6 w-6 text-yellow-500" />
                  Contract Insights ({insights.length})
                </h2>
                <button
                  onClick={handleGenerateInsights}
                  disabled={generatingInsights}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  <RefreshCw className={`h-5 w-5 ${generatingInsights ? 'animate-spin' : ''}`} />
                  {generatingInsights ? 'Generating...' : 'Regenerate Insights'}
                </button>
              </div>

              <div className="space-y-3">
                {insights.map((insight) => (
                  <div
                    key={insight.id}
                    className={`border-2 rounded-lg p-4 ${getSeverityColor(insight.severity)}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {getSeverityIcon(insight.severity)}
                          <h3 className="font-semibold">{insight.title}</h3>
                          <span className="px-2 py-0.5 text-xs rounded-full bg-white bg-opacity-50">
                            {insight.severity}
                          </span>
                        </div>
                        <p className="text-sm mb-3">{insight.description}</p>

                        {insight.actionable && insight.suggestedActions && insight.suggestedActions.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium mb-1">Suggested Actions:</p>
                            <ul className="text-sm space-y-1 ml-4">
                              {insight.suggestedActions.map((action, idx) => (
                                <li key={idx} className="list-disc">{action}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleAcknowledgeInsight(insight.id)}
                          className="p-2 hover:bg-white hover:bg-opacity-30 rounded transition-colors"
                          title="Acknowledge insight"
                        >
                          <CheckCircle2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDismissInsight(insight.id)}
                          className="p-2 hover:bg-white hover:bg-opacity-30 rounded transition-colors"
                          title="Dismiss insight"
                        >
                          <XCircle className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Generate Insights Button (when no insights) */}
          {insights.length === 0 && !loading && contracts.length > 0 && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Lightbulb className="h-8 w-8 text-blue-600" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Contract Insights</h3>
                    <p className="text-sm text-gray-600">
                      Analyze processed contracts to detect cost-saving opportunities, service overlaps, and compliance risks
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {extractionCounts.processed} of {totalContracts} contracts processed and ready for analysis
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleOpenInsightsModal}
                    disabled={generatingInsights || extractionCounts.processed === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors"
                  >
                    <RefreshCw className={`h-5 w-5 ${generatingInsights ? 'animate-spin' : ''}`} />
                    {generatingInsights ? 'Analyzing...' : 'Select Contracts'}
                  </button>
                  <button
                    onClick={handleAnalyzeAllContracts}
                    disabled={generatingInsights || extractionCounts.processed === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    <RefreshCw className={`h-5 w-5 ${generatingInsights ? 'animate-spin' : ''}`} />
                    {generatingInsights ? 'Analyzing...' : `Analyze All (${extractionCounts.processed})`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Processing Tab Content */}
      {activeTab === 'processing' && (
        <>
          {/* Upload Section */}
      <div className="mb-6 bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Bulk Upload</h2>
          <div className="flex gap-3">
            <label className="cursor-pointer">
              <input
                type="file"
                multiple
                accept=".pdf,.docx,.txt"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploadingFiles}
              />
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${uploadingFiles ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'} text-white`}>
                <Upload className="h-5 w-5" />
                <span>{uploadingFiles ? 'Uploading...' : 'Upload Files'}</span>
              </div>
            </label>
            <label className="cursor-pointer">
              <input
                type="file"
                // @ts-ignore - webkitdirectory is not in TypeScript types but widely supported
                webkitdirectory=""
                directory=""
                multiple
                onChange={handleFolderUpload}
                className="hidden"
                disabled={uploadingFiles}
              />
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${uploadingFiles ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 cursor-pointer'} text-white`}>
                <FileText className="h-5 w-5" />
                <span>{uploadingFiles ? 'Uploading...' : 'Upload Folder'}</span>
              </div>
            </label>
            <button
              onClick={handleProcessAllPending}
              disabled={reprocessingContracts || uploadingFiles}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${reprocessingContracts || uploadingFiles ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-500 hover:bg-gray-600 cursor-pointer'} text-white`}
              title="Background processor handles pending contracts automatically. Use this only to re-process."
            >
              <RefreshCw className={`h-5 w-5 ${reprocessingContracts ? 'animate-spin' : ''}`} />
              <span>{reprocessingContracts ? 'Re-processing...' : 'Re-Process'}</span>
            </button>
          </div>
        </div>

        {/* Background Processing Info */}
        {totalPendingCount > 0 && !reprocessingContracts && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">
                  Background Processing Active
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  {(() => {
                    const totalContracts = totalPendingCount + totalProcessedCount
                    const percentProcessed = totalContracts > 0
                      ? ((totalProcessedCount / totalContracts) * 100).toFixed(1)
                      : '0.0'

                    const contractsPerRun = 3
                    const minutesPerRun = 5
                    const totalRuns = Math.ceil(totalPendingCount / contractsPerRun)
                    const totalMinutes = totalRuns * minutesPerRun
                    const hours = Math.floor(totalMinutes / 60)
                    const minutes = totalMinutes % 60

                    let timeEstimate = ''
                    if (hours > 0) {
                      timeEstimate = `~${hours}h ${minutes}m`
                    } else {
                      timeEstimate = `~${minutes} minutes`
                    }

                    return `Processing ${percentProcessed}% (${totalProcessedCount} of ${totalContracts}) • ${totalPendingCount} pending • 2 every 1 min • ETA: ${timeEstimate}`
                  })()}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  💡 The "Re-Process" button is only needed if you want to manually re-trigger extraction for pending contracts.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress Indicator */}
        {uploadProgress && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Uploading batch {uploadProgress.current} of {uploadProgress.total}
              </span>
              <span className="text-sm text-gray-500">
                {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        <p className="text-sm text-gray-600">
          Supported formats: PDF, DOCX, TXT • Max size: 50MB per file • Batch size: 10 files
        </p>
        <p className="text-xs text-gray-500 mt-2">
          💡 Folder upload automatically detects years from folder structure (e.g., /2023/, /contracts-2024/)
        </p>
      </div>

      {/* Re-processing Progress */}
      {reprocessProgress && (
        <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-purple-900">
              Re-processing contract {reprocessProgress.current} of {reprocessProgress.total}
            </span>
            <span className="text-sm text-purple-700">
              {Math.round((reprocessProgress.current / reprocessProgress.total) * 100)}%
            </span>
          </div>
          <div className="w-full bg-purple-200 rounded-full h-2.5">
            <div
              className="bg-purple-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${(reprocessProgress.current / reprocessProgress.total) * 100}%` }}
            ></div>
          </div>
          <p className="text-xs text-purple-700 mt-2">
            Processing sequentially with delays to avoid Lambda limits...
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex gap-2 flex-wrap">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            filter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Contracts ({totalContracts > 0 ? totalContracts : contracts.length})
        </button>
        <button
          onClick={() => setFilter('active')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            filter === 'active'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Active
        </button>

        {/* Contract Category Filters */}
        <div className="h-6 w-px bg-gray-300 mx-1"></div>
        <button
          onClick={() => setContractCategoryFilter('all')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            contractCategoryFilter === 'all'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Types
        </button>
        <button
          onClick={() => setContractCategoryFilter('vendors')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            contractCategoryFilter === 'vendors'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Vendors
        </button>
        <button
          onClick={() => setContractCategoryFilter('customers')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            contractCategoryFilter === 'customers'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Customers
        </button>
        <button
          onClick={() => setContractCategoryFilter('duplicates')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            contractCategoryFilter === 'duplicates'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Duplicates
        </button>

        {/* Extraction Status Filters - Client-side only */}
        <div className="h-6 w-px bg-gray-300 mx-1"></div>
        <button
          onClick={() => setExtractionStatusFilter('all')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            extractionStatusFilter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Status
        </button>
        <button
          onClick={() => setExtractionStatusFilter('pending')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            extractionStatusFilter === 'pending'
              ? 'bg-yellow-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Pending ({totalPendingCount || 0})
        </button>
        <button
          onClick={() => setExtractionStatusFilter('processing')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            extractionStatusFilter === 'processing'
              ? 'bg-orange-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Processing ({contracts.filter(c => c.extractionStatus === 'processing').length})
        </button>
        <button
          onClick={() => setExtractionStatusFilter('processed')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            extractionStatusFilter === 'processed'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Processed ({totalProcessedCount || 0})
        </button>
        <button
          onClick={() => setExtractionStatusFilter('failed')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            extractionStatusFilter === 'failed'
              ? 'bg-red-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Failed ({contracts.filter(c => c.extractionStatus === 'failed').length})
        </button>
        <button
          onClick={() => setFilter('expiring')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            filter === 'expiring'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Expiring Soon
        </button>
        <button
          onClick={() => setFilter('archived')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            filter === 'archived'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Archived
        </button>

        {/* Renewal Year Filter */}
        <div className="h-6 w-px bg-gray-300 mx-1"></div>
        <div className="flex items-center gap-2">
          <label htmlFor="renewal-year-filter" className="text-sm text-gray-700">
            Renewal Year:
          </label>
          <select
            id="renewal-year-filter"
            value={renewalYearFilter}
            onChange={(e) => setRenewalYearFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Years</option>
            <option value={new Date().getFullYear().toString()}>{new Date().getFullYear()} and later</option>
            <option value={(new Date().getFullYear() - 1).toString()}>{new Date().getFullYear() - 1} and later</option>
            <option value={(new Date().getFullYear() - 2).toString()}>{new Date().getFullYear() - 2} and later</option>
          </select>
        </div>

        {/* Vendor Search Filter */}
        <div className="h-6 w-px bg-gray-300 mx-1"></div>
        <div className="flex items-center gap-2">
          <label htmlFor="vendor-search" className="text-sm text-gray-700">
            Vendor:
          </label>
          <input
            id="vendor-search"
            type="text"
            placeholder="Search vendors..."
            value={vendorSearchTerm}
            onChange={(e) => setVendorSearchTerm(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 placeholder-gray-400 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
          />
          {vendorSearchTerm && (
            <button
              onClick={() => setVendorSearchTerm('')}
              className="text-gray-400 hover:text-gray-600"
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        {/* Document Type Filter */}
        <div className="h-6 w-px bg-gray-300 mx-1"></div>
        <div className="flex items-center gap-2">
          <label htmlFor="document-type-filter" className="text-sm text-gray-700">
            Document Type:
          </label>
          <select
            id="document-type-filter"
            value={documentTypeFilter}
            onChange={(e) => setDocumentTypeFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            <option value="EMPTY">Empty/Missing</option>
            <option value="VENDOR_CONTRACT">Vendor Contract</option>
            <option value="MUTUAL_NDA">Mutual NDA</option>
            <option value="INVESTMENT_DOCUMENT">Investment Document</option>
            <option value="OTHER">Other</option>
          </select>
        </div>

        {/* Date Filter - From Current Date Only */}
        <div className="h-6 w-px bg-gray-300 mx-1"></div>
        <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 cursor-pointer transition-colors">
          <input
            type="checkbox"
            checked={fromCurrentDateOnly}
            onChange={(e) => setFromCurrentDateOnly(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">From today onwards</span>
        </label>

        {/* Seats Filter - Only Contracts with Seats */}
        <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 cursor-pointer transition-colors">
          <input
            type="checkbox"
            checked={hasSeatsOnly}
            onChange={(e) => setHasSeatsOnly(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Has seat count</span>
        </label>

        {/* Seat Count Comparison Filter */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white">
          <span className="text-sm text-gray-700 whitespace-nowrap">Seats</span>
          <select
            value={seatCountOperator}
            onChange={(e) => setSeatCountOperator(e.target.value as '>' | '<' | '=' | 'all')}
            className="text-sm border-gray-300 rounded focus:ring-2 focus:ring-blue-500 px-2 py-1"
          >
            <option value="all">Any</option>
            <option value=">">&gt;</option>
            <option value="<">&lt;</option>
            <option value="=">=</option>
          </select>
          <input
            type="number"
            min="0"
            value={seatCountValue}
            onChange={(e) => setSeatCountValue(parseInt(e.target.value) || 0)}
            disabled={seatCountOperator === 'all'}
            className="w-20 text-sm border-gray-300 rounded focus:ring-2 focus:ring-blue-500 px-2 py-1 disabled:bg-gray-100 disabled:text-gray-400"
            placeholder="0"
          />
        </div>

        {/* Filtered Contract Count */}
        <div className="h-6 w-px bg-gray-300 mx-1"></div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">
            Showing: <span className="text-blue-600">{(() => {
              // Calculate filtered count based on all active filters
              let filtered = contracts

              // Exclude archived and duplicates by default
              if (filter !== 'archived') {
                filtered = filtered.filter(c => !c.isArchived)
              }
              if (activeTab !== 'duplicates') {
                filtered = filtered.filter(c => !c.isDuplicate)
              }

              // Main filter (expiring)
              if (filter === 'expiring') {
                filtered = filtered.filter(c => {
                  if (!c.expirationDate) return false
                  const expirationDate = new Date(c.expirationDate)
                  const daysUntilExpiration = Math.floor((expirationDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                  return daysUntilExpiration >= 0 && daysUntilExpiration <= 90
                })
              }

              // Extraction status filter
              if (extractionStatusFilter !== 'all') {
                filtered = filtered.filter(c => {
                  if (extractionStatusFilter === 'processed') {
                    return c.extractionStatus === 'processed' || c.extractionStatus === 'completed'
                  }
                  return c.extractionStatus === extractionStatusFilter
                })
              }

              // Renewal year filter
              if (renewalYearFilter !== 'all') {
                const filterYear = parseInt(renewalYearFilter)
                filtered = filtered.filter(c => {
                  if (!c.renewalDate) return false
                  const renewalYear = new Date(c.renewalDate).getFullYear()
                  return renewalYear >= filterYear
                })
              }

              // Document type filter
              if (documentTypeFilter !== 'all') {
                filtered = filtered.filter(c => {
                  const docType = safeExtractValue<string>(c.documentType)
                  if (documentTypeFilter === 'EMPTY') {
                    return !docType || docType === ''
                  }
                  return docType === documentTypeFilter
                })
              }

              // Vendor search filter
              if (vendorSearchTerm.trim()) {
                filtered = filtered.filter(c => {
                  const vendorName = safeExtractValue<string>(c.vendorName) || ''
                  return vendorName.toLowerCase().includes(vendorSearchTerm.toLowerCase())
                })
              }

              // Date filter - from current date onwards
              if (fromCurrentDateOnly) {
                const today = new Date()
                today.setHours(0, 0, 0, 0) // Start of today
                filtered = filtered.filter(c => {
                  // Check renewal/expiration date
                  if (c.renewalDate) {
                    const renewalDate = new Date(c.renewalDate)
                    if (renewalDate >= today) return true
                  }
                  if (c.expirationDate) {
                    const expirationDate = new Date(c.expirationDate)
                    if (expirationDate >= today) return true
                  }
                  // Check effective/contract date
                  const effectiveDate = c.extractedMetadata?.dates?.effectiveDate?.value
                  if (effectiveDate) {
                    const contractDate = new Date(effectiveDate)
                    if (contractDate >= today) return true
                  }
                  return false
                })
              }

              // Seats filter - only contracts with seat counts
              if (hasSeatsOnly) {
                filtered = filtered.filter(c => {
                  const seats = safeExtractValue<number>(c.numberOfLicenses)
                  return seats !== undefined && seats !== null && seats > 0
                })
              }

              // Seat count comparison filter
              if (seatCountOperator !== 'all' && seatCountValue > 0) {
                filtered = filtered.filter(c => {
                  const seats = safeExtractValue<number>(c.numberOfLicenses)
                  if (seats === undefined || seats === null) return false

                  switch (seatCountOperator) {
                    case '>':
                      return seats > seatCountValue
                    case '<':
                      return seats < seatCountValue
                    case '=':
                      return seats === seatCountValue
                    default:
                      return true
                  }
                })
              }

              return filtered.length
            })()}</span> {(() => {
              let filtered = contracts
              // Exclude archived and duplicates by default
              if (filter !== 'archived') {
                filtered = filtered.filter(c => !c.isArchived)
              }
              if (activeTab !== 'duplicates') {
                filtered = filtered.filter(c => !c.isDuplicate)
              }
              if (filter === 'expiring') {
                filtered = filtered.filter(c => {
                  if (!c.expirationDate) return false
                  const expirationDate = new Date(c.expirationDate)
                  const daysUntilExpiration = Math.floor((expirationDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                  return daysUntilExpiration >= 0 && daysUntilExpiration <= 90
                })
              }
              if (extractionStatusFilter !== 'all') {
                filtered = filtered.filter(c => {
                  if (extractionStatusFilter === 'processed') {
                    return c.extractionStatus === 'processed' || c.extractionStatus === 'completed'
                  }
                  return c.extractionStatus === extractionStatusFilter
                })
              }
              if (renewalYearFilter !== 'all') {
                const filterYear = parseInt(renewalYearFilter)
                filtered = filtered.filter(c => {
                  if (!c.renewalDate) return false
                  const renewalYear = new Date(c.renewalDate).getFullYear()
                  return renewalYear >= filterYear
                })
              }
              if (documentTypeFilter !== 'all') {
                filtered = filtered.filter(c => {
                  return c.documentType === documentTypeFilter
                })
              }
              // Date filter - from current date onwards
              if (fromCurrentDateOnly) {
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                filtered = filtered.filter(c => {
                  if (c.renewalDate) {
                    const renewalDate = new Date(c.renewalDate)
                    if (renewalDate >= today) return true
                  }
                  if (c.expirationDate) {
                    const expirationDate = new Date(c.expirationDate)
                    if (expirationDate >= today) return true
                  }
                  const effectiveDate = c.extractedMetadata?.dates?.effectiveDate?.value
                  if (effectiveDate) {
                    const contractDate = new Date(effectiveDate)
                    if (contractDate >= today) return true
                  }
                  return false
                })
              }
              // Seats filter - only contracts with seat counts
              if (hasSeatsOnly) {
                filtered = filtered.filter(c => {
                  const seats = safeExtractValue<number>(c.numberOfLicenses)
                  return seats !== undefined && seats !== null && seats > 0
                })
              }
              return filtered.length === 1 ? 'contract' : 'contracts'
            })()}
          </span>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading contracts...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Contracts List */}
      {!loading && !error && contracts.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No contracts found</h3>
          <p className="text-gray-600">Upload your first contract to get started</p>
        </div>
      )}

      {!loading && !error && contracts.length > 0 && (() => {
        // Apply client-side filtering (main filter + extraction status)
        let filteredContracts = contracts

        // Exclude archived contracts by default (unless explicitly viewing archived filter)
        // This matches Dashboard behavior and ensures metrics are consistent
        if (filter !== 'archived') {
          filteredContracts = filteredContracts.filter(c => !c.isArchived)
        }

        // Exclude duplicate contracts by default (unless on duplicates tab)
        if (activeTab !== 'duplicates') {
          filteredContracts = filteredContracts.filter(c => !c.isDuplicate)
        }

        // First apply main filter (expiring)
        if (filter === 'expiring') {
          filteredContracts = filteredContracts.filter(c => {
            if (!c.expirationDate) return false
            const expirationDate = new Date(c.expirationDate)
            const daysUntilExpiration = Math.floor((expirationDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
            // Show contracts expiring within 90 days and not already expired
            return daysUntilExpiration >= 0 && daysUntilExpiration <= 90
          })
        }

        // Then apply extraction status filtering
        if (extractionStatusFilter !== 'all') {
          filteredContracts = filteredContracts.filter(c => {
            if (extractionStatusFilter === 'processed') {
              // Handle both 'processed' and old 'completed' status during migration
              return c.extractionStatus === 'processed' || c.extractionStatus === 'completed'
            }
            return c.extractionStatus === extractionStatusFilter
          })
        }

        // Apply renewal year filter (exclude past years)
        if (renewalYearFilter !== 'all') {
          const filterYear = parseInt(renewalYearFilter)
          filteredContracts = filteredContracts.filter(c => {
            if (!c.renewalDate) return false // Exclude contracts without renewal date
            const renewalYear = new Date(c.renewalDate).getFullYear()
            return renewalYear >= filterYear // Show contracts with renewal year >= selected year
          })
        }

        // Apply document type filter
        if (documentTypeFilter !== 'all') {
          filteredContracts = filteredContracts.filter(c => {
            const docType = safeExtractValue<string>(c.documentType)
            if (documentTypeFilter === 'EMPTY') {
              return !docType || docType === ''
            }
            return docType === documentTypeFilter
          })
        }

        // Apply vendor search filter
        if (vendorSearchTerm.trim()) {
          filteredContracts = filteredContracts.filter(c => {
            const vendorName = safeExtractValue<string>(c.vendorName) || ''
            return vendorName.toLowerCase().includes(vendorSearchTerm.toLowerCase())
          })
        }

        // Apply date filter - from current date onwards
        if (fromCurrentDateOnly) {
          const today = new Date()
          today.setHours(0, 0, 0, 0) // Start of today
          filteredContracts = filteredContracts.filter(c => {
            // Check renewal/expiration date
            if (c.renewalDate) {
              const renewalDate = new Date(c.renewalDate)
              if (renewalDate >= today) return true
            }
            if (c.expirationDate) {
              const expirationDate = new Date(c.expirationDate)
              if (expirationDate >= today) return true
            }
            // Check effective/contract date
            const effectiveDate = c.extractedMetadata?.dates?.effectiveDate?.value
            if (effectiveDate) {
              const contractDate = new Date(effectiveDate)
              if (contractDate >= today) return true
            }
            return false
          })
        }

        // Apply seats filter - only contracts with seat counts
        if (hasSeatsOnly) {
          filteredContracts = filteredContracts.filter(c => {
            const seats = safeExtractValue<number>(c.numberOfLicenses)
            return seats !== undefined && seats !== null && seats > 0
          })
        }

        // Apply seat count comparison filter
        if (seatCountOperator !== 'all' && seatCountValue > 0) {
          filteredContracts = filteredContracts.filter(c => {
            const seats = safeExtractValue<number>(c.numberOfLicenses)
            if (seats === undefined || seats === null) return false

            switch (seatCountOperator) {
              case '>':
                return seats > seatCountValue
              case '<':
                return seats < seatCountValue
              case '=':
                return seats === seatCountValue
              default:
                return true
            }
          })
        }

        // Apply sorting if a column is selected
        if (sortColumn) {
          filteredContracts = [...filteredContracts].sort((a, b) => {
            let aValue: any
            let bValue: any

            switch (sortColumn) {
              case 'status':
                aValue = a.extractionStatus || ''
                bValue = b.extractionStatus || ''
                break
              case 'vendor':
                aValue = (safeExtractValue<string>(a.vendorName) || '').toLowerCase()
                bValue = (safeExtractValue<string>(b.vendorName) || '').toLowerCase()
                break
              case 'documentType':
                aValue = (safeExtractValue<string>(a.documentType) || 'zzz').toLowerCase()
                bValue = (safeExtractValue<string>(b.documentType) || 'zzz').toLowerCase()
                break
              case 'fileName':
                aValue = a.fileName.toLowerCase()
                bValue = b.fileName.toLowerCase()
                break
              case 'value':
                aValue = safeExtractValue<number>(a.contractValue) || 0
                bValue = safeExtractValue<number>(b.contractValue) || 0
                break
              case 'seats':
                aValue = safeExtractValue<number>(a.numberOfLicenses) || 0
                bValue = safeExtractValue<number>(b.numberOfLicenses) || 0
                break
              case 'renewal':
                aValue = a.expirationDate ? new Date(a.expirationDate).getTime() : 0
                bValue = b.expirationDate ? new Date(b.expirationDate).getTime() : 0
                break
              case 'uploaded':
                aValue = new Date(a.uploadedAt).getTime()
                bValue = new Date(b.uploadedAt).getTime()
                break
              case 'contractDate':
                aValue = a.extractedMetadata?.dates?.effectiveDate?.value ? new Date(a.extractedMetadata.dates.effectiveDate.value).getTime() : 0
                bValue = b.extractedMetadata?.dates?.effectiveDate?.value ? new Date(b.extractedMetadata.dates.effectiveDate.value).getTime() : 0
                break
              default:
                return 0
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
            return 0
          })
        }

        if (filteredContracts.length === 0) {
          return (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No contracts match this filter</h3>
              <p className="text-gray-600">Try selecting a different extraction status</p>
            </div>
          )
        }

        return (
        <>
          {/* Bulk Action Toolbar */}
          {selectedContractIds.size > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Edit className="h-5 w-5 text-blue-600" />
                  <h3 className="text-sm font-medium text-gray-900">
                    Bulk Edit {selectedContractIds.size} Contract{selectedContractIds.size > 1 ? 's' : ''}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setSelectedContractIds(new Set())
                    setBulkEditDocumentType('')
                    setBulkEditITSpend(null)
                    setBulkEditArchive(null)
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                {/* Document Type Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                  <select
                    value={bulkEditDocumentType}
                    onChange={(e) => setBulkEditDocumentType(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- No Change --</option>
                    <option value="VENDOR_CONTRACT">Vendor Contract</option>
                    <option value="MUTUAL_NDA">Mutual NDA</option>
                    <option value="INVESTMENT_DOCUMENT">Investment Document</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                {/* IT Spend Toggle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IT Spend</label>
                  <select
                    value={bulkEditITSpend === null ? '' : bulkEditITSpend ? 'true' : 'false'}
                    onChange={(e) => setBulkEditITSpend(e.target.value === '' ? null : e.target.value === 'true')}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- No Change --</option>
                    <option value="true">Mark as IT Spend</option>
                    <option value="false">Unmark as IT Spend</option>
                  </select>
                </div>

                {/* Archive Toggle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Archive Status</label>
                  <select
                    value={bulkEditArchive === null ? '' : bulkEditArchive ? 'true' : 'false'}
                    onChange={(e) => setBulkEditArchive(e.target.value === '' ? null : e.target.value === 'true')}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- No Change --</option>
                    <option value="true">Archive Contracts</option>
                    <option value="false">Unarchive Contracts</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleBulkUpdate}
                  disabled={bulkUpdating || (bulkEditDocumentType === '' && bulkEditITSpend === null && bulkEditArchive === null)}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {bulkUpdating ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Apply Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedContractIds.size === filteredContracts.length && filteredContracts.length > 0}
                      onChange={handleToggleAllContracts}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      title="Select all"
                    />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      {sortColumn === 'status' && (
                        sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('vendor')}
                >
                  <div className="flex items-center gap-1">
                    Vendor
                    {sortColumn === 'vendor' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('documentType')}
                >
                  <div className="flex items-center gap-1">
                    Document Type
                    {sortColumn === 'documentType' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('fileName')}
                >
                  <div className="flex items-center gap-1">
                    File Name
                    {sortColumn === 'fileName' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('contractDate')}
                >
                  <div className="flex items-center gap-1">
                    Contract Date
                    {sortColumn === 'contractDate' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('value')}
                >
                  <div className="flex items-center gap-1">
                    Value
                    {sortColumn === 'value' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IT Spend
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('seats')}
                >
                  <div className="flex items-center gap-1">
                    Seats
                    {sortColumn === 'seats' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('renewal')}
                >
                  <div className="flex items-center gap-1">
                    Renewal
                    {sortColumn === 'renewal' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('uploaded')}
                >
                  <div className="flex items-center gap-1">
                    Uploaded
                    {sortColumn === 'uploaded' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContracts.map((contract) => (
                <tr key={contract.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedContractIds.has(contract.id)}
                      onChange={() => handleToggleContract(contract.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(contract)}
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(contract.extractionStatus)}`}>
                        {contract.extractionStatus}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Building className="h-5 w-5 text-gray-400 mr-3" />
                      <div className="text-sm text-gray-900">
                        {safeExtractValue<string>(contract.vendorName) || <span className="text-gray-400 italic">Not extracted</span>}
                      </div>
                      {contractsBeingExtracted.has(contract.id) && (
                        <RefreshCw className="h-4 w-4 text-blue-500 animate-spin inline-block ml-2" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                    <div className="text-sm text-gray-700">
                      {(() => {
                        const docType = safeExtractValue<string>(contract.documentType)

                        const typeMap: Record<string, { label: string; badge: string }> = {
                          'VENDOR_CONTRACT': { label: 'Vendor Contract', badge: 'bg-blue-100 text-blue-800' },
                          'MUTUAL_NDA': { label: 'Mutual NDA', badge: 'bg-purple-100 text-purple-800' },
                          'INVESTMENT_DOCUMENT': { label: 'Investment Doc', badge: 'bg-green-100 text-green-800' },
                          'OTHER': { label: 'Other', badge: 'bg-gray-100 text-gray-800' }
                        }

                        if (!docType) {
                          return (
                            <select
                              value=""
                              onChange={async (e) => {
                                const newType = e.target.value
                                if (!newType) return

                                console.log('📄 [Document Type] Setting type for contract:', {
                                  contractId: contract.id,
                                  vendor: contract.vendorName,
                                  newType
                                })

                                // Optimistic update
                                setContracts(prev => prev.map(c =>
                                  c.id === contract.id ? { ...c, documentType: newType } : c
                                ))

                                // Make API call
                                try {
                                  const response = await fetch(cacheBust(`/api/tprm/contracts/${contract.id}`), createFetchOptions({
                                    method: 'PUT',
                                    body: JSON.stringify({ updates: { documentType: newType } })
                                  }))

                                  if (!response.ok) {
                                    throw new Error('Failed to update document type')
                                  }

                                  const result = await response.json()
                                  console.log('📄 [Document Type] Updated successfully:', result)

                                  // Update with server response
                                  if (result.contract) {
                                    setContracts(prev => prev.map(c =>
                                      c.id === contract.id ? result.contract : c
                                    ))
                                  }
                                } catch (err) {
                                  console.error('📄 [Document Type] Error:', err)
                                  // Revert on error
                                  setContracts(prev => prev.map(c =>
                                    c.id === contract.id ? { ...c, documentType: undefined } : c
                                  ))
                                  alert('Failed to update document type')
                                }
                              }}
                              className="text-xs px-2 py-1 border border-gray-300 rounded bg-white hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                            >
                              <option value="">-- Select Type --</option>
                              <option value="VENDOR_CONTRACT">Vendor Contract</option>
                              <option value="MUTUAL_NDA">Mutual NDA</option>
                              <option value="INVESTMENT_DOCUMENT">Investment Document</option>
                              <option value="OTHER">Other</option>
                            </select>
                          )
                        }

                        const typeInfo = typeMap[docType] || { label: docType, badge: 'bg-gray-100 text-gray-800' }
                        return (
                          <span className={`px-2 py-1 text-xs rounded-full ${typeInfo.badge}`}>
                            {typeInfo.label}
                          </span>
                        )
                      })()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-gray-400 mr-3" />
                      <div className="text-sm text-gray-700">
                        {contract.fileName}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {contract.extractedMetadata?.dates?.effectiveDate?.value ? (
                        formatDate(contract.extractedMetadata.dates.effectiveDate.value)
                      ) : (
                        <span className="text-gray-400 italic">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900">
                      <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                      {formatCurrency(safeExtractValue<number>(contract.contractValue), safeExtractValue<string>(contract.contractCurrency))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={contract.isITSpend || false}
                      onChange={async (e) => {
                        const newValue = e.target.checked
                        const previousValue = contract.isITSpend || false

                        console.log('💰 [IT Spend] Checkbox changed:', {
                          contractId: contract.id,
                          vendor: contract.vendorName,
                          previousValue,
                          newValue
                        })

                        // Optimistic update - update UI immediately
                        setContracts(prev => prev.map(c =>
                          c.id === contract.id ? { ...c, isITSpend: newValue } : c
                        ))

                        // Make API call in background
                        try {
                          console.log('💰 [IT Spend] Sending PUT request to /api/tprm/contracts/' + contract.id)
                          const response = await fetch(cacheBust(`/api/tprm/contracts/${contract.id}`), createFetchOptions({
                            method: 'PUT',
                            body: JSON.stringify({ updates: { isITSpend: newValue } })
                          }))
                          console.log('💰 [IT Spend] Response status:', response.status)

                          if (!response.ok) {
                            const errorText = await response.text()
                            console.error('💰 [IT Spend] API error:', errorText)
                            throw new Error('Failed to update IT Spend flag')
                          }

                          const result = await response.json()
                          console.log('💰 [IT Spend] Success! Result:', result)

                          // Update with server response to ensure consistency
                          if (result.contract) {
                            setContracts(prev => prev.map(c =>
                              c.id === contract.id ? result.contract : c
                            ))
                          }
                        } catch (err) {
                          console.error('💰 [IT Spend] Error:', err)
                          // Revert to previous value on error
                          setContracts(prev => prev.map(c =>
                            c.id === contract.id ? { ...c, isITSpend: previousValue } : c
                          ))
                          alert('Failed to update IT Spend classification')
                        }
                      }}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                      title="Mark as IT spend for spend per employee calculations"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {safeExtractValue<number>(contract.numberOfLicenses) ? (
                        <span>{safeExtractValue<number>(contract.numberOfLicenses)!.toLocaleString()}</span>
                      ) : (
                        <span className="text-gray-400 italic">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center text-sm text-gray-900">
                        <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                        {formatDate(contract.renewalDate || contract.expirationDate)}
                      </div>
                      {contract.renewalDate && (() => {
                        const daysUntil = getDaysUntilRenewal(contract.renewalDate)
                        if (daysUntil !== null) {
                          return (
                            <div className="text-xs text-gray-500">
                              {daysUntil > 0 ? `${daysUntil} days` : daysUntil === 0 ? 'Today' : `${Math.abs(daysUntil)} days ago`}
                            </div>
                          )
                        }
                        return null
                      })()}
                      {contract.renewalStatus && contract.renewalStatus !== 'active' && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getRenewalStatusBadge(contract.renewalStatus)}`}>
                          {contract.renewalStatus.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(contract.uploadedAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setSelectedContract(contract)}
                        className="text-blue-600 hover:text-blue-700"
                        title="View details"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      {(contract.extractionStatus === 'pending' || contract.extractionStatus === 'processing' || contract.extractionStatus === 'failed') && (
                        <button
                          onClick={() => triggerExtraction(contract.id)}
                          className="text-purple-600 hover:text-purple-700"
                          title={contract.extractionStatus === 'failed' ? 'Retry extraction' : contract.extractionStatus === 'processing' ? 'Re-process (stuck)' : 'Start extraction'}
                          disabled={extractingContracts.has(contract.id)}
                        >
                          <RefreshCw className={`h-5 w-5 ${extractingContracts.has(contract.id) ? 'animate-spin' : ''}`} />
                        </button>
                      )}
                      {contract.renewalDate && contract.renewalStatus !== 'non_renew_scheduled' && !contract.nonRenewalNotificationSent && (
                        <button
                          onClick={() => handleScheduleNonRenewal(contract)}
                          className="text-orange-600 hover:text-orange-700"
                          title="Schedule non-renewal"
                        >
                          <BellOff className="h-5 w-5" />
                        </button>
                      )}
                      {contract.renewalStatus === 'non_renew_scheduled' && !contract.nonRenewalNotificationSent && (
                        <button
                          onClick={() => handleCancelNonRenewal(contract.id)}
                          className="text-green-600 hover:text-green-700"
                          title="Cancel non-renewal"
                        >
                          <Bell className="h-5 w-5" />
                        </button>
                      )}
                      {!contract.isArchived ? (
                        <button
                          onClick={() => handleArchiveContract(contract.id)}
                          className="text-gray-600 hover:text-gray-700"
                          title="Archive contract"
                        >
                          <Archive className="h-5 w-5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUnarchiveContract(contract.id)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Unarchive contract"
                        >
                          <RefreshCw className="h-5 w-5" />
                        </button>
                      )}
                      {!contract.isDuplicate ? (
                        <button
                          onClick={() => handleMarkDuplicate(contract.id)}
                          className="text-orange-600 hover:text-orange-700"
                          title="Mark as duplicate"
                        >
                          <Copy className="h-5 w-5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUnmarkDuplicate(contract.id)}
                          className="text-purple-600 hover:text-purple-700"
                          title="Unmark duplicate"
                        >
                          <RefreshCw className="h-5 w-5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteContract(contract.id)}
                        className="text-red-600 hover:text-red-700"
                        title="Delete contract"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Load More Button */}
        {hasMore && (() => {
          // Calculate the correct total based on active filters
          let displayTotal = totalContracts

          // If filtering by extraction status, use the extraction counts
          if (extractionStatusFilter !== 'all') {
            if (extractionStatusFilter === 'pending') {
              displayTotal = extractionCounts.pending
            } else if (extractionStatusFilter === 'processing') {
              displayTotal = extractionCounts.processing
            } else if (extractionStatusFilter === 'processed') {
              displayTotal = extractionCounts.processed
            } else if (extractionStatusFilter === 'failed') {
              displayTotal = extractionCounts.failed
            }
          }

          return (
            <div className="mt-6 text-center">
              <button
                onClick={loadMoreContracts}
                disabled={loadingMore}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
              >
                {loadingMore ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading...
                  </>
                ) : (
                  <>
                    Load More ({filteredContracts.length} of {displayTotal})
                  </>
                )}
              </button>
            </div>
          )
        })()}

        {/* Floating Action Bar for Multi-Select */}
        {selectedContractIds.size > 0 && (
          <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40">
            <div className="bg-gray-900 text-white rounded-lg shadow-2xl px-6 py-4 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{selectedContractIds.size}</span>
                <span className="text-gray-300">contract{selectedContractIds.size !== 1 ? 's' : ''} selected</span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleReprocessSelected}
                  disabled={reprocessingContracts}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${reprocessingContracts ? 'animate-spin' : ''}`} />
                  {reprocessingContracts ? 'Processing...' : 'Process Selected'}
                </button>
                <button
                  onClick={handleGenerateInsights}
                  disabled={generatingInsights}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Lightbulb className={`h-4 w-4 ${generatingInsights ? 'animate-spin' : ''}`} />
                  {generatingInsights ? 'Analyzing...' : 'Generate Insights'}
                </button>
                <button
                  onClick={() => setSelectedContractIds(new Set())}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
        </>
        )
      })()}
        </>
      )}

      {/* Duplicates Tab Content */}
      {activeTab === 'duplicates' && (
        <div className="p-6 space-y-6" data-tour="contracts-duplicates-panel">
          {/* Detection Header */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Duplicate Detection</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Find and manage duplicate contracts based on vendor name, dates, and contract value
                </p>
              </div>
              <button
                onClick={detectDuplicates}
                disabled={detectingDuplicates}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-tour="contracts-detect-button"
              >
                <Search className="h-5 w-5" />
                {detectingDuplicates ? 'Detecting...' : 'Detect Duplicates'}
              </button>
            </div>

            {/* Detection Results Summary */}
            {duplicateDetectionResults && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>Detection complete:</strong> Found {duplicateDetectionResults.duplicatesFound} duplicate contracts
                  {duplicateDetectionResults.duplicateGroups.length > 0 && (
                    <span> across {duplicateDetectionResults.duplicateGroups.length} groups</span>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Duplicates List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="text-gray-600 mt-2">Loading duplicates...</p>
            </div>
          ) : contracts.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Archive className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Duplicates Found</h3>
              <p className="text-gray-600 mb-4">
                Click "Detect Duplicates" to scan for duplicate contracts
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Duplicate Contracts ({contracts.length})
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  These contracts were identified as duplicates and excluded from analytics
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contract Value
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dates
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploaded
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Marked By
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {contracts.map((contract) => (
                      <tr key={contract.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {safeExtractValue<string>(contract.vendorName) || 'Unknown'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {contract.fileName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {safeExtractValue<number>(contract.contractValue)
                              ? `$${safeExtractValue<number>(contract.contractValue)!.toLocaleString()}`
                              : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {contract.effectiveDate && (
                              <div>Start: {new Date(contract.effectiveDate).toLocaleDateString()}</div>
                            )}
                            {contract.expirationDate && (
                              <div>End: {new Date(contract.expirationDate).toLocaleDateString()}</div>
                            )}
                            {!contract.effectiveDate && !contract.expirationDate && '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(contract.uploadedAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {contract.markedDuplicateBy || '-'}
                          </div>
                          {contract.markedDuplicateAt && (
                            <div className="text-xs text-gray-400">
                              {new Date(contract.markedDuplicateAt).toLocaleDateString()}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => unmarkDuplicate(contract.id)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            title="Restore this contract"
                          >
                            Restore
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Are you sure you want to permanently delete this duplicate contract?')) {
                                deleteContract(contract.id)
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Delete this contract permanently"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settings Tab Content */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Contract Management Settings</h2>
              <p className="text-sm text-gray-600 mt-1">
                Configure settings for contract analytics and dashboards
              </p>
            </div>

            {/* Employee Count Settings */}
            <div className="space-y-4">
              {/* Year-over-Year Employee Counts */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Employee Count by Year
                </label>
                <p className="text-xs text-gray-600 mb-3">
                  Track employee count changes over time for accurate year-over-year spend per employee calculations
                </p>

                {/* Existing Year Entries */}
                {contractSettings?.employeeCountByYear && contractSettings.employeeCountByYear.length > 0 ? (
                  <div className="space-y-2 mb-3">
                    {contractSettings.employeeCountByYear
                      .map((entry, originalIndex) => ({ entry, originalIndex }))
                      .sort((a, b) => b.entry.year.localeCompare(a.entry.year))
                      .map(({ entry, originalIndex }, displayIndex) => (
                      <div key={`year-entry-${displayIndex}`} className="flex items-center gap-3 bg-gray-50 p-3 rounded-md">
                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-sm text-gray-500">Year</label>
                            <input
                              type="text"
                              value={entry.year}
                              onChange={(e) => {
                                const updated = [...(contractSettings.employeeCountByYear || [])]
                                updated[originalIndex] = {
                                  ...entry,
                                  year: e.target.value
                                }
                                setContractSettings({
                                  ...contractSettings,
                                  employeeCountByYear: updated
                                })
                              }}
                              placeholder="e.g., 2024"
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-gray-500">Employee Count</label>
                            <input
                              type="text"
                              value={entry.count}
                              onChange={(e) => {
                                const updated = [...(contractSettings.employeeCountByYear || [])]
                                const newValue = parseInt(e.target.value)
                                updated[originalIndex] = {
                                  ...entry,
                                  count: isNaN(newValue) ? entry.count : (newValue || 1)
                                }
                                setContractSettings({
                                  ...contractSettings,
                                  employeeCountByYear: updated
                                })
                              }}
                              placeholder="e.g., 250"
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const updated = contractSettings.employeeCountByYear!.filter((_, idx) => idx !== originalIndex)
                            setContractSettings({
                              ...contractSettings,
                              employeeCountByYear: updated.length > 0 ? updated : undefined
                            })
                          }}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Remove year"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic mb-3">No year-specific employee counts configured yet</p>
                )}

                {/* Add New Year Button */}
                <button
                  onClick={() => {
                    const currentYear = new Date().getFullYear().toString()
                    const existingYears = (contractSettings?.employeeCountByYear || []).map(e => e.year)
                    setContractSettings({
                      ...contractSettings,
                      employeeCountByYear: [
                        ...(contractSettings?.employeeCountByYear || []),
                        { year: currentYear, count: contractSettings?.employeeCount || 1 }
                      ]
                    })
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Add Year
                </button>
              </div>

              {/* Legacy Single Employee Count (backward compatibility) */}
              {!contractSettings?.employeeCountByYear || contractSettings.employeeCountByYear.length === 0 ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Employee Count
                  </label>
                  <input
                    type="text"
                    value={contractSettings?.employeeCount || 1}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value)
                      setContractSettings({
                        ...contractSettings,
                        employeeCount: isNaN(newValue) ? (contractSettings?.employeeCount || 1) : (newValue || 1)
                      })
                    }}
                    className="w-64 px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., 250"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Used for all years when year-specific counts are not configured
                  </p>
                </div>
              ) : null}

              {/* Save Button */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={saveContractSettings}
                  disabled={savingSettings}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {savingSettings ? (
                    <>
                      <Settings className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : settingsSaved ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Saved!
                    </>
                  ) : (
                    <>
                      <Settings className="h-4 w-4" />
                      Save Settings
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contract Detail Modal */}
      {selectedContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedContract.vendorName || selectedContract.fileName}
                </h2>
                {selectedContract.vendorName && (
                  <p className="text-sm text-gray-500">{selectedContract.fileName}</p>
                )}
              </div>
              <button
                onClick={() => setSelectedContract(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4 space-y-6">
              {/* Extraction Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  {getStatusIcon(selectedContract)}
                  <h3 className="font-semibold">Extraction Status</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Status: <span className={`px-2 py-1 rounded-full ${getStatusBadge(selectedContract.extractionStatus)}`}>
                    {selectedContract.extractionStatus}
                  </span>
                </p>
                {selectedContract.extractionError && (
                  <p className="text-sm text-red-600 mt-2">Error: {selectedContract.extractionError}</p>
                )}
              </div>

              {/* TPRM Integration */}
              {selectedContract.onboardingStatus !== 'not_started' && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="h-5 w-5 text-blue-600" />
                    <h3 className="font-semibold text-blue-900">TPRM Integration</h3>
                  </div>
                  <p className="text-sm text-blue-800">
                    Onboarding Status: <span className="font-medium">{selectedContract.onboardingStatus}</span>
                  </p>
                  {selectedContract.subprocessorId && (
                    <p className="text-sm text-blue-800 mt-1">
                      Linked to Subprocessor: <code className="bg-blue-100 px-2 py-1 rounded">{selectedContract.subprocessorId}</code>
                    </p>
                  )}
                  {selectedContract.dataProcessingTerms?.isDpa && (
                    <p className="text-sm text-blue-800 mt-1">
                      ✓ DPA Detected - Automatically classified as subprocessor
                    </p>
                  )}
                </div>
              )}

              {/* Financial Information */}
              {selectedContract.contractValue && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Financial Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Contract Value</p>
                      <p className="font-medium">{formatCurrency(selectedContract.contractValue, selectedContract.contractCurrency)}</p>
                    </div>
                    {selectedContract.numberOfLicenses && (
                      <div>
                        <p className="text-sm text-gray-500">Licenses</p>
                        <p className="font-medium">{selectedContract.numberOfLicenses} seats</p>
                      </div>
                    )}
                    {selectedContract.pricePerLicense && (
                      <div>
                        <p className="text-sm text-gray-500">Price per License</p>
                        <p className="font-medium">{formatCurrency(selectedContract.pricePerLicense, selectedContract.contractCurrency)}</p>
                      </div>
                    )}
                    {selectedContract.paymentTerms && (
                      <div>
                        <p className="text-sm text-gray-500">Payment Terms</p>
                        <p className="font-medium">{selectedContract.paymentTerms}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contract Dates */}
              {(selectedContract.effectiveDate || selectedContract.expirationDate) && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Contract Dates
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedContract.effectiveDate && (
                      <div>
                        <p className="text-sm text-gray-500">Effective Date</p>
                        <p className="font-medium">{formatDate(selectedContract.effectiveDate)}</p>
                      </div>
                    )}
                    {selectedContract.expirationDate && (
                      <div>
                        <p className="text-sm text-gray-500">Expiration Date</p>
                        <p className="font-medium">{formatDate(selectedContract.expirationDate)}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-500">Auto-Renew</p>
                      <p className="font-medium">{selectedContract.autoRenew ? 'Yes' : 'No'}</p>
                    </div>
                    {selectedContract.renewalNoticeDays && (
                      <div>
                        <p className="text-sm text-gray-500">Non-Renewal Notice</p>
                        <p className="font-medium">{selectedContract.renewalNoticeDays} days</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Service Information */}
              {selectedContract.serviceCategory && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Building className="h-5 w-5" />
                    Service Information
                  </h3>
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-gray-500">Category</p>
                      <p className="font-medium">{selectedContract.serviceCategory}</p>
                    </div>
                    {selectedContract.serviceSubcategory && (
                      <div>
                        <p className="text-sm text-gray-500">Subcategory</p>
                        <p className="font-medium">{selectedContract.serviceSubcategory}</p>
                      </div>
                    )}
                    {selectedContract.primaryFunction && (
                      <div>
                        <p className="text-sm text-gray-500">Primary Function</p>
                        <p className="font-medium">{selectedContract.primaryFunction}</p>
                      </div>
                    )}
                    {selectedContract.featureSet && selectedContract.featureSet.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Key Features</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedContract.featureSet.map((feature, idx) => (
                            <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contacts */}
              {selectedContract.contacts && selectedContract.contacts.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Contacts
                  </h3>
                  <div className="space-y-3">
                    {selectedContract.contacts.map((contact) => (
                      <div key={contact.id} className="border-l-4 border-blue-500 pl-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{contact.name}</p>
                            {contact.title && <p className="text-sm text-gray-500">{contact.title}</p>}
                          </div>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                            {contact.type}
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          {contact.email && <p>📧 {contact.email}</p>}
                          {contact.phone && <p>📞 {contact.phone}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SLA Terms */}
              {selectedContract.slaTerms && selectedContract.slaTerms.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">SLA Terms</h3>
                  <div className="space-y-2">
                    {selectedContract.slaTerms.map((sla, idx) => (
                      <div key={idx} className="bg-gray-50 rounded p-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{sla.metric}</span>
                          <span className="text-blue-600">{sla.target}</span>
                        </div>
                        {sla.penalty && (
                          <p className="text-sm text-gray-500 mt-1">Penalty: {sla.penalty}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Data Processing */}
              {selectedContract.dataProcessingTerms && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Data Processing</h3>
                  <div className="space-y-2">
                    {selectedContract.dataProcessingTerms.isDpa && (
                      <p className="text-sm">
                        <span className="font-medium">DPA Included:</span> Yes
                      </p>
                    )}
                    {selectedContract.dataProcessingTerms.dataTypes && (
                      <p className="text-sm">
                        <span className="font-medium">Data Types:</span> {selectedContract.dataProcessingTerms.dataTypes.join(', ')}
                      </p>
                    )}
                    {selectedContract.dataLocation && (
                      <p className="text-sm">
                        <span className="font-medium">Data Location:</span> {selectedContract.dataLocation}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setSelectedContract(null)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              {selectedContract.subprocessorId && (
                <a
                  href="/admin#tprm"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  View in TPRM
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Non-Renewal Confirmation Modal */}
      {nonRenewalContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Schedule Non-Renewal</h3>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-900">{nonRenewalContract.vendorName || nonRenewalContract.fileName}</p>
              {nonRenewalContract.renewalDate && (
                <p className="text-sm text-gray-600 mt-1">
                  Renewal Date: {formatDate(nonRenewalContract.renewalDate)}
                </p>
              )}
              {nonRenewalContract.renewalNoticeDays && (
                <p className="text-sm text-gray-600">
                  Notice Required: {nonRenewalContract.renewalNoticeDays} days
                </p>
              )}
            </div>

            <p className="text-sm text-gray-600 mb-4">
              This will schedule a non-renewal notice to be sent to the vendor according to the contract terms.
              {renewalSettings?.requireApprovalForNonRenewal && (
                <span className="block mt-2 text-orange-600">
                  ⚠️ This action requires approval from: {renewalSettings.nonRenewalApprovers.join(', ')}
                </span>
              )}
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setNonRenewalContract(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleConfirmNonRenewal()}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                Schedule Non-Renewal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Renewal Settings Modal */}
      {showRenewalSettings && renewalSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Renewal Settings</h3>
              <button
                onClick={() => setShowRenewalSettings(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Auto Reminders */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Automatic Reminders</h4>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={renewalSettings.enableAutoReminders}
                    onChange={(e) => setRenewalSettings({ ...renewalSettings, enableAutoReminders: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Enable automatic renewal reminders</span>
                </label>

                {renewalSettings.enableAutoReminders && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reminder Schedule (days before renewal)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Current: {renewalSettings.reminderSchedule.join(', ')} days
                    </p>
                    <p className="text-xs text-gray-500">
                      Reminders will be sent at these intervals before the renewal date.
                    </p>
                  </div>
                )}
              </div>

              {/* Reminder Recipients */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Reminder Recipients</h4>
                <div className="space-y-2">
                  {renewalSettings.reminderRecipients.map((email, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-sm text-gray-700">{email}</span>
                    </div>
                  ))}
                  {renewalSettings.reminderRecipients.length === 0 && (
                    <p className="text-sm text-gray-500 italic">No recipients configured</p>
                  )}
                </div>
              </div>

              {/* Non-Renewal Approval */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Non-Renewal Workflow</h4>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={renewalSettings.requireApprovalForNonRenewal}
                    onChange={(e) => setRenewalSettings({ ...renewalSettings, requireApprovalForNonRenewal: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Require approval for non-renewals</span>
                </label>

                {renewalSettings.requireApprovalForNonRenewal && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Approvers
                    </label>
                    <div className="space-y-2">
                      {renewalSettings.nonRenewalApprovers.map((email, idx) => (
                        <div key={idx} className="text-sm text-gray-700">{email}</div>
                      ))}
                      {renewalSettings.nonRenewalApprovers.length === 0 && (
                        <p className="text-sm text-gray-500 italic">No approvers configured</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* TPRM Integration */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">TPRM Integration</h4>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={renewalSettings.autoCreateTPRMOnboarding}
                    onChange={(e) => setRenewalSettings({ ...renewalSettings, autoCreateTPRMOnboarding: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Auto-onboard vendors with DPAs to TPRM</span>
                </label>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowRenewalSettings(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(cacheBust('/api/tprm/contracts/settings'), createFetchOptions({
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(renewalSettings)
                    }))
                    if (response.ok) {
                      alert('Settings saved successfully!')
                      setShowRenewalSettings(false)
                    }
                  } catch (err) {
                    alert('Failed to save settings')
                  }
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Insights Generation Modal */}
      {showInsightsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <Lightbulb className="h-6 w-6 text-blue-600" />
                <h3 className="text-xl font-semibold text-gray-900">Select Contracts to Analyze</h3>
              </div>
              <button
                onClick={() => setShowInsightsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {(() => {
                // Filter to only show processed contracts
                const processedContracts = contracts.filter(c =>
                  c.extractionStatus === 'processed' || c.extractionStatus === 'completed'
                )

                return (
                  <>
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        Only processed contracts can be analyzed for insights. Pending contracts will be analyzed automatically once processing is complete.
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="flex items-center gap-2 p-3 bg-blue-50 border-2 border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedContractIds.size === processedContracts.length && processedContracts.length > 0}
                          onChange={handleToggleAllContracts}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="font-semibold text-blue-900">
                          Select All ({processedContracts.length} processed contracts)
                        </span>
                      </label>
                    </div>

                    {processedContracts.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <p>No processed contracts available for analysis.</p>
                        <p className="text-sm mt-2">Please wait for contracts to be processed.</p>
                      </div>
                    )}

                    <div className="space-y-2">
                      {processedContracts.map((contract) => (
                  <label
                    key={contract.id}
                    className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedContractIds.has(contract.id)}
                      onChange={() => handleToggleContract(contract.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{contract.fileName}</div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        {safeExtractValue<string>(contract.vendorName) && (
                          <span className="flex items-center gap-1">
                            <Building className="h-3 w-3" />
                            {safeExtractValue<string>(contract.vendorName)}
                          </span>
                        )}
                        {safeExtractValue<number>(contract.contractValue) && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            ${safeExtractValue<number>(contract.contractValue)!.toLocaleString()}
                          </span>
                        )}
                        {contract.renewalDate && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(contract.renewalDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                      ))}
                    </div>
                  </>
                )
              })()}
            </div>

            <div className="p-6 border-t bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600">
                  {selectedContractIds.size} processed contract{selectedContractIds.size !== 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowInsightsModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowInsightsModal(false)
                    handleReprocessSelected()
                  }}
                  disabled={selectedContractIds.size === 0}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Re-process ({selectedContractIds.size})
                </button>
                <button
                  onClick={handleGenerateInsights}
                  disabled={selectedContractIds.size === 0}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Generate Insights ({selectedContractIds.size})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  )
}
